import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Clock, Sparkles, Users, Calendar, Plus, MoreHorizontal, Building, Bell, CreditCard, Palette, Loader2, CheckCircle, X, Trash2, MapPin, Edit2, Tag, ChevronLeft, ChevronRight, Copy, RefreshCw, Search, Info, Mail, MessageCircle, Star, BarChart3 } from "lucide-react";
import { format } from "date-fns";
import { MapPicker } from "@/components/maps/MapPicker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useBusiness } from "@/hooks/use-business";
import { useAuth } from "@/contexts/AuthContext";
import { useStaff } from "@/hooks/use-staff";
import { useServices } from "@/hooks/use-services";
import { useServiceCategories } from "@/hooks/use-service-categories";
import { useLocations } from "@/hooks/use-locations";
import { useBilling } from "@/hooks/use-billing";
import { useSubscription } from "@/hooks/use-subscription";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePwaUpdate } from "@/hooks/use-pwa-update";
import type { Tables } from "@/integrations/supabase/types";
import { Checkbox } from "@/components/ui/checkbox";
import { cleanPhoneNumber, phoneToE164 } from "@/lib/phone-utils";
import { getBaseUrl } from "@/lib/site";
import { EnablePushNotifications } from "@/components/push/EnablePushNotifications";
import { isNativeIosApp } from "@/lib/platform";
import { openBuukdSupportWhatsApp } from "@/lib/support";

async function geocodeAddressFallback(address: string): Promise<{ lat: number; lng: number } | null> {
  const q = String(address || "").trim();
  if (!q) return null;
  try {
    // Used only as a fallback when Google Maps is blocked/unavailable.
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`,
      { method: "GET" },
    );
    if (!res.ok) return null;
    const json: any = await res.json();
    const first = Array.isArray(json) ? json[0] : null;
    const lat = first?.lat != null ? Number(first.lat) : NaN;
    const lng = first?.lon != null ? Number(first.lon) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  } catch {
    return null;
  }
}

type AppRole = Tables<"staff">["role"];
type SocialPlatform = "instagram" | "facebook" | "twitter" | "linkedin" | "tiktok" | "whatsapp";
type AddableSocialPlatform = Exclude<SocialPlatform, "whatsapp">;
type SettingsSectionId =
  | "business"
  | "usage"
  | "schedule"
  | "staff"
  | "booking"
  | "payment"
  | "coupons"
  | "locations"
  | "notifications"
  | "support";

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ANDROID_PACKAGE_NAME = "co.buukd.app";
const IOS_APP_STORE_ID = String(import.meta.env.VITE_IOS_APP_STORE_ID || "").trim();
const IOS_APP_STORE_REVIEW_URL = IOS_APP_STORE_ID
  ? `itms-apps://itunes.apple.com/app/id${IOS_APP_STORE_ID}?action=write-review`
  : "https://apps.apple.com/search?term=Buukd";
const PLAY_STORE_REVIEW_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`;

interface DaySchedule {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

interface BreakTime {
  id?: string; // For existing breaks from DB
  startTime: string;
  endTime: string;
  name?: string;
}

function OrganizationUsageSettings() {
  const { business } = useBusiness();
  const { metrics, loading } = useBilling();
  const primaryModule = String((business as any)?.primary_module || "appointments");
  const isCatering = primaryModule === "catering_services" || primaryModule === "food_vendors";
  const bookingLabel = isCatering ? "Orders" : "Appointments";

  const usageItems = [
    { label: bookingLabel, used: metrics.appointmentsThisMonth, limit: metrics.appointmentLimit, icon: Calendar },
    { label: "Staff Members", used: metrics.staffCount, limit: metrics.staffLimit, icon: Users },
    { label: "Locations", used: metrics.locationsCount, limit: metrics.locationLimit, icon: MapPin },
    { label: "Customers", used: metrics.customersCount, limit: metrics.customerLimit, icon: Users },
    { label: "SMS Campaigns", used: metrics.smsCampaignsThisMonth, limit: metrics.smsCampaignLimit, icon: MessageCircle },
    { label: "Email Campaigns", used: metrics.emailCampaignsThisMonth, limit: metrics.emailCampaignLimit, icon: Mail },
  ];

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {usageItems.map((item) => {
        const Icon = item.icon;
        const isUnlimited = item.limit === null;
        const percentage = item.limit ? Math.min(100, (item.used / item.limit) * 100) : 0;

        return (
          <Card key={item.label}>
            <CardContent className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-foreground sm:text-base">{item.label}</p>
                  <p className="text-xs text-muted-foreground">Current period</p>
                  <div className="mt-3 flex items-baseline justify-between gap-3">
                    <span className="text-2xl font-bold text-foreground">{item.used}</span>
                    <span className="text-sm text-muted-foreground">
                      {isUnlimited ? "Unlimited" : `/ ${item.limit}`}
                    </span>
                  </div>
                  {!isUnlimited && (
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${percentage}%` }} />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

const defaultSchedule: DaySchedule[] = DAYS.map((_, index) => ({
  isOpen: index !== 0, // Sunday closed by default
  openTime: "09:00",
  closeTime: "17:00",
}));

const SOCIAL_PLATFORM_OPTIONS: Array<{
  platform: SocialPlatform;
  label: string;
  placeholder: string;
}> = [
  { platform: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourprofile" },
  { platform: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourpage" },
  { platform: "twitter", label: "X / Twitter", placeholder: "https://twitter.com/yourhandle" },
  { platform: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/yourcompany" },
  { platform: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@yourhandle" },
  { platform: "whatsapp", label: "WhatsApp", placeholder: "https://wa.me/233XXXXXXXXX" },
];

type PaymentRegion = "ghana" | "nigeria" | "kenya" | "south_africa" | "united_states" | "united_kingdom" | "other";
type DirectPaymentMethod = "bank" | "momo" | "mpesa" | "venmo" | "cashapp" | "other";

const PAYMENT_REGIONS: Array<{ value: PaymentRegion; label: string }> = [
  { value: "ghana", label: "Ghana" },
  { value: "nigeria", label: "Nigeria" },
  { value: "kenya", label: "Kenya" },
  { value: "south_africa", label: "South Africa" },
  { value: "united_states", label: "United States" },
  { value: "united_kingdom", label: "United Kingdom" },
  { value: "other", label: "Other / International" },
];

const REGION_METHODS: Record<PaymentRegion, DirectPaymentMethod[]> = {
  ghana: ["bank", "momo", "other"],
  nigeria: ["bank", "other"],
  kenya: ["mpesa", "bank", "other"],
  south_africa: ["bank", "mpesa", "other"],
  united_states: ["venmo", "cashapp", "bank", "other"],
  united_kingdom: ["bank", "other"],
  other: ["bank", "other"],
};

const METHOD_LABELS: Record<DirectPaymentMethod, string> = {
  bank: "Bank transfer",
  momo: "Mobile Money",
  mpesa: "M-Pesa",
  venmo: "Venmo",
  cashapp: "Cash App",
  other: "Other instructions",
};

const REGION_BANKS: Record<PaymentRegion, Array<{ value: string; label: string }>> = {
  ghana: [
    { value: "gcb", label: "GCB Bank" },
    { value: "ecobank_ghana", label: "Ecobank Ghana" },
    { value: "stanbic_ghana", label: "Stanbic Bank Ghana" },
    { value: "absa_ghana", label: "Absa Bank Ghana" },
    { value: "cal", label: "CAL Bank" },
    { value: "fidelity_ghana", label: "Fidelity Bank Ghana" },
    { value: "first_national_ghana", label: "First National Bank Ghana" },
    { value: "gtbank_ghana", label: "Guaranty Trust Bank Ghana" },
    { value: "republic_ghana", label: "Republic Bank Ghana" },
    { value: "standard_chartered_ghana", label: "Standard Chartered Bank Ghana" },
    { value: "uba_ghana", label: "United Bank for Africa Ghana" },
    { value: "zenith_ghana", label: "Zenith Bank Ghana" },
    { value: "access_ghana", label: "Access Bank Ghana" },
    { value: "adb_ghana", label: "Agricultural Development Bank" },
  ],
  nigeria: [
    { value: "access_nigeria", label: "Access Bank" },
    { value: "gtbank_nigeria", label: "GTBank" },
    { value: "zenith_nigeria", label: "Zenith Bank" },
    { value: "uba_nigeria", label: "United Bank for Africa" },
    { value: "first_bank_nigeria", label: "First Bank of Nigeria" },
    { value: "fidelity_nigeria", label: "Fidelity Bank" },
    { value: "stanbic_ibtc", label: "Stanbic IBTC Bank" },
    { value: "sterling_bank", label: "Sterling Bank" },
    { value: "union_bank_nigeria", label: "Union Bank" },
    { value: "wema_bank", label: "Wema Bank" },
    { value: "kuda", label: "Kuda Bank" },
    { value: "opay", label: "OPay" },
    { value: "palmpay", label: "PalmPay" },
    { value: "moniepoint", label: "Moniepoint" },
  ],
  kenya: [
    { value: "equity_kenya", label: "Equity Bank Kenya" },
    { value: "kcb", label: "KCB Bank" },
    { value: "cooperative_kenya", label: "Co-operative Bank of Kenya" },
    { value: "absa_kenya", label: "Absa Bank Kenya" },
    { value: "stanbic_kenya", label: "Stanbic Bank Kenya" },
    { value: "ncba", label: "NCBA Bank" },
  ],
  south_africa: [
    { value: "fnb_sa", label: "First National Bank" },
    { value: "absa_sa", label: "Absa Bank" },
    { value: "standard_bank_sa", label: "Standard Bank" },
    { value: "nedbank", label: "Nedbank" },
    { value: "capitec", label: "Capitec Bank" },
    { value: "investec", label: "Investec" },
  ],
  united_states: [
    { value: "chase", label: "Chase" },
    { value: "bank_of_america", label: "Bank of America" },
    { value: "wells_fargo", label: "Wells Fargo" },
    { value: "citi", label: "Citi" },
    { value: "capital_one", label: "Capital One" },
    { value: "us_bank", label: "U.S. Bank" },
  ],
  united_kingdom: [
    { value: "barclays", label: "Barclays" },
    { value: "hsbc_uk", label: "HSBC UK" },
    { value: "lloyds", label: "Lloyds Bank" },
    { value: "natwest", label: "NatWest" },
    { value: "monzo", label: "Monzo" },
    { value: "starling", label: "Starling Bank" },
    { value: "revolut_uk", label: "Revolut" },
  ],
  other: [],
};

const MOBILE_MONEY_OPTIONS: Record<PaymentRegion, Array<{ value: string; label: string }>> = {
  ghana: [
    { value: "mtn_momo", label: "MTN Mobile Money" },
    { value: "telecel_cash", label: "Telecel Cash" },
    { value: "airteltigo_money", label: "AirtelTigo Money" },
  ],
  nigeria: [],
  kenya: [{ value: "mpesa_kenya", label: "M-Pesa Kenya" }],
  south_africa: [{ value: "mpesa_south_africa", label: "M-Pesa South Africa" }],
  united_states: [],
  united_kingdom: [],
  other: [],
};

function inferPaymentRegion(country: string, currencyCode: string): PaymentRegion {
  const normalized = `${country} ${currencyCode}`.toLowerCase();
  if (normalized.includes("ghana") || normalized.includes("ghs")) return "ghana";
  if (normalized.includes("nigeria") || normalized.includes("ngn")) return "nigeria";
  if (normalized.includes("kenya") || normalized.includes("kes")) return "kenya";
  if (normalized.includes("south africa") || normalized.includes("zar")) return "south_africa";
  if (normalized.includes("united states") || normalized.includes("usa") || normalized.includes("usd")) return "united_states";
  if (normalized.includes("united kingdom") || normalized.includes("uk") || normalized.includes("gbp")) return "united_kingdom";
  return "other";
}

function optionLabel(options: Array<{ value: string; label: string }>, value: string) {
  return options.find((option) => option.value === value)?.label || value;
}

function buildDirectPaymentInstructions({
  method,
  region,
  businessName,
  bank,
  accountNumber,
  mobileNetwork,
  mobileNumber,
  fallbackInstructions,
}: {
  method: DirectPaymentMethod;
  region: PaymentRegion;
  businessName: string;
  bank: string;
  accountNumber: string;
  mobileNetwork: string;
  mobileNumber: string;
  fallbackInstructions: string;
}) {
  const name = businessName || "the business";
  const bankName = optionLabel(REGION_BANKS[region] || [], bank);
  const mobileName = optionLabel(MOBILE_MONEY_OPTIONS[region] || [], mobileNetwork);

  if (method === "bank") {
    return [
      "Pay by bank transfer",
      bankName ? `Bank: ${bankName}` : null,
      `Account name: ${name}`,
      accountNumber ? `Account number: ${accountNumber}` : "Account number: [add account number]",
      "Use your booking code as the payment reference.",
      "Send payment proof to the business after payment.",
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (method === "momo" || method === "mpesa") {
    return [
      `Pay by ${method === "mpesa" ? "M-Pesa" : mobileName || "Mobile Money"}`,
      mobileName && method !== "mpesa" ? `Network: ${mobileName}` : null,
      `Account name: ${name}`,
      mobileNumber ? `Number: ${mobileNumber}` : "Number: [add payment number]",
      "Use your booking code as the payment reference.",
      "Send payment proof to the business after payment.",
    ]
      .filter(Boolean)
      .join("\n");
  }

  if (method === "venmo") {
    return [
      "Pay by Venmo",
      `Account name: ${name}`,
      mobileNumber ? `Venmo handle: ${mobileNumber}` : "Venmo handle: [add Venmo handle]",
      "Use your booking code as the payment note.",
    ].join("\n");
  }

  if (method === "cashapp") {
    return [
      "Pay by Cash App",
      `Account name: ${name}`,
      mobileNumber ? `Cash App tag: ${mobileNumber}` : "Cash App tag: [add Cash App tag]",
      "Use your booking code as the payment note.",
    ].join("\n");
  }

  return fallbackInstructions.trim();
}

export default function Settings() {
  const { business, loading: businessLoading, refetch } = useBusiness();
  const { user } = useAuth();
  const { staff, loading: staffLoading, createStaff, updateStaff, deleteStaff } = useStaff();
  const { locations, loading: locationsLoading, refetch: refetchLocations } = useLocations();
  const { currentPlan } = useSubscription();
  const { toast } = useToast();
  const pwaUpdate = usePwaUpdate();
  const isNativeIos = isNativeIosApp();
  const [schedule, setSchedule] = useState<DaySchedule[]>(defaultSchedule);
  const [breakTimes, setBreakTimes] = useState<Record<number, BreakTime[]>>({}); // day_of_week -> breaks
  const [breakTimeDialogOpen, setBreakTimeDialogOpen] = useState(false);
  const [breakTimeDialogDay, setBreakTimeDialogDay] = useState<number | null>(null);
  const [editingBreakTime, setEditingBreakTime] = useState<BreakTime | null>(null);
  const [breakTimeForm, setBreakTimeForm] = useState<BreakTime>({ startTime: "12:00", endTime: "13:00", name: "" });
  const [currency, setCurrency] = useState<string>("GHS");
  const [businessName, setBusinessName] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [timezone, setTimezone] = useState("Africa/Accra");
  const [isSaving, setIsSaving] = useState(false);
  const [activeSettingsSection, setActiveSettingsSection] = useState<SettingsSectionId | null>(null);
  const [settingsSearch, setSettingsSearch] = useState("");
  const [supportBusinessName, setSupportBusinessName] = useState("");
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSending, setSupportSending] = useState(false);
  const [ordersEnabled, setOrdersEnabled] = useState(false);
  const [eventCateringRequestsEnabled, setEventCateringRequestsEnabled] = useState(false);
  const [ordersMultipleMenusEnabled, setOrdersMultipleMenusEnabled] = useState(false);
  const [accessDeskEnabled, setAccessDeskEnabled] = useState(true);
  const [appointmentsInvoicingEnabled, setAppointmentsInvoicingEnabled] = useState(false);
  const [logBookEnabled, setLogBookEnabled] = useState(false);
  const [salesEnabled, setSalesEnabled] = useState(false);
  const [smsRemindersEnabled, setSmsRemindersEnabled] = useState(true);
  const ACCENT_THEMES = ["blue", "pink", "purple", "lavender", "teal", "navy", "charcoal"] as const;
  type AccentTheme = (typeof ACCENT_THEMES)[number];
  const [accentTheme, setAccentTheme] = useState<AccentTheme>("blue");

  // Business contact + socials (persisted)
  const [phoneCountryCode, setPhoneCountryCode] = useState<string>("+233");
  const [businessPhoneLocal, setBusinessPhoneLocal] = useState<string>("");
  const [whatsappUrl, setWhatsappUrl] = useState<string>("");
  const [facebookUrl, setFacebookUrl] = useState<string>("");
  const [twitterUrl, setTwitterUrl] = useState<string>("");
  const [instagramUrl, setInstagramUrl] = useState<string>("");
  const [primarySocialLabels, setPrimarySocialLabels] = useState<Record<
    "instagram" | "facebook" | "twitter" | "linkedin" | "tiktok" | "whatsapp",
    string
  >>({
    instagram: "",
    facebook: "",
    twitter: "",
    linkedin: "",
    tiktok: "",
    whatsapp: "",
  });
  const [linkedinUrl, setLinkedinUrl] = useState<string>("");
  const [tiktokUrl, setTiktokUrl] = useState<string>("");
  const [primarySocialDialogOpen, setPrimarySocialDialogOpen] = useState(false);
  const [primarySocialPlatform, setPrimarySocialPlatform] = useState<AddableSocialPlatform>("instagram");
  const [primarySocialLabelDraft, setPrimarySocialLabelDraft] = useState("");
  const [primarySocialUrlDraft, setPrimarySocialUrlDraft] = useState("");

  // Optional: additional social accounts per platform
  const [extraSocialLinks, setExtraSocialLinks] = useState<any[]>([]);
  const [showExtraLinksSection, setShowExtraLinksSection] = useState(false);
  const [socialDialogOpen, setSocialDialogOpen] = useState(false);
  const [socialPlatform, setSocialPlatform] = useState<SocialPlatform>("instagram");
  const [socialLabel, setSocialLabel] = useState("");
  const [socialUrlNew, setSocialUrlNew] = useState("");
  const [isSavingSocial, setIsSavingSocial] = useState(false);

  // Optional: additional logos (kept separate from primary business.logo_url)
  const [extraLogos, setExtraLogos] = useState<any[]>([]);
  const [extraLogoFile, setExtraLogoFile] = useState<File | null>(null);
  const [isSavingExtraLogo, setIsSavingExtraLogo] = useState(false);
  const extraLogoInputRef = useRef<HTMLInputElement>(null);
  const [locationAddress, setLocationAddress] = useState<string>("");
  const [locationCity, setLocationCity] = useState<string>("");
  const [locationCountry, setLocationCountry] = useState<string>("");
  const [locationPlaceId, setLocationPlaceId] = useState<string>("");
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [defaultLocationId, setDefaultLocationId] = useState<string | null>(null);

  // Payment settings state
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [paymentRegion, setPaymentRegion] = useState<PaymentRegion>("ghana");
  const [paymentRegionManuallyChanged, setPaymentRegionManuallyChanged] = useState(false);
  const [paymentBank, setPaymentBank] = useState<string>("");
  const [paymentAccountNumber, setPaymentAccountNumber] = useState<string>("");
  const [paymentMobileMoneyNetwork, setPaymentMobileMoneyNetwork] = useState<string>("");
  const [paymentMobileMoneyNumber, setPaymentMobileMoneyNumber] = useState<string>("");
  const [collectBookingDeposits, setCollectBookingDeposits] = useState<boolean>(true);
  const [allowFullPaymentAtBooking, setAllowFullPaymentAtBooking] = useState<boolean>(false);
  const [bookingDepositPaymentInstructions, setBookingDepositPaymentInstructions] = useState<string>("");
  const [foodOrderPaymentInstructions, setFoodOrderPaymentInstructions] = useState<string>("");
  const [businessPaystackEnabled, setBusinessPaystackEnabled] = useState<boolean>(false);
  const [businessPaystackMasked, setBusinessPaystackMasked] = useState<string>("");
  const [businessPaystackSecretKey, setBusinessPaystackSecretKey] = useState<string>("");
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  // Coupons state
  const [coupons, setCoupons] = useState<any[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponCodeNew, setCouponCodeNew] = useState("");
  const [couponTypeNew, setCouponTypeNew] = useState<"percent" | "amount">("percent");
  const [couponValueNew, setCouponValueNew] = useState("");
  const [couponStartsNew, setCouponStartsNew] = useState("");
  const [couponEndsNew, setCouponEndsNew] = useState("");
  const [couponActiveNew, setCouponActiveNew] = useState(true);
  const [isSavingCoupon, setIsSavingCoupon] = useState(false);
  const [couponServiceIdsNew, setCouponServiceIdsNew] = useState<string[]>([]);
  const [couponCategoryIdsNew, setCouponCategoryIdsNew] = useState<string[]>([]);
  const [couponServiceIdsByCouponId, setCouponServiceIdsByCouponId] = useState<Record<string, string[]>>({});
  const [couponCategoryIdsByCouponId, setCouponCategoryIdsByCouponId] = useState<Record<string, string[]>>({});
  const [couponUsesByCouponId, setCouponUsesByCouponId] = useState<Record<string, { total: number; active: number; completed: number }>>({});

  // Location management state
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [locationFormName, setLocationFormName] = useState("");
  const [locationFormAddress, setLocationFormAddress] = useState("");
  const [locationFormCity, setLocationFormCity] = useState("");
  const [locationFormCountry, setLocationFormCountry] = useState("");
  const [locationFormPlaceId, setLocationFormPlaceId] = useState("");
  const [locationFormLat, setLocationFormLat] = useState<number | null>(null);
  const [locationFormLng, setLocationFormLng] = useState<number | null>(null);
  const [isSavingLocation, setIsSavingLocation] = useState(false);

  // Booking page customization state
  const [primaryColor, setPrimaryColor] = useState<string>("#2563EB");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [logoRemoved, setLogoRemoved] = useState<boolean>(false);
  const [headerMedia, setHeaderMedia] = useState<
    Array<{ id: string; file: File; preview: string; type: "image" | "video" }>
  >([]);
  const headerMediaInputRef = useRef<HTMLInputElement>(null);
  const [googleReviewUrl, setGoogleReviewUrl] = useState<string>("");
  const [clientPhotosUrl, setClientPhotosUrl] = useState<string>("");
  const [cancellationPolicy, setCancellationPolicy] = useState<string>("");
  const [termsAndConditions, setTermsAndConditions] = useState<string>("");
  const [multiServiceBookingEnabled, setMultiServiceBookingEnabled] = useState<boolean>(false);
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Staff editing state
  const { services } = useServices();
  const { categories: serviceCategories } = useServiceCategories();
  const [isStaffDialogOpen, setIsStaffDialogOpen] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formRole, setFormRole] = useState<AppRole>("staff");

  // Staff hours management state
  const [isHoursDialogOpen, setIsHoursDialogOpen] = useState(false);
  const [managingHoursStaffId, setManagingHoursStaffId] = useState<string | null>(null);
  const [staffHours, setStaffHours] = useState<Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_off: boolean;
  }>>([]);

  // Staff services assignment state
  const [isServicesDialogOpen, setIsServicesDialogOpen] = useState(false);
  const [assigningServicesStaffId, setAssigningServicesStaffId] = useState<string | null>(null);
  const [assignedServiceIds, setAssignedServiceIds] = useState<string[]>([]);

  // Optional recurring weekly hours for one service.
  const [serviceHoursDialogOpen, setServiceHoursDialogOpen] = useState(false);
  const [serviceHoursServiceId, setServiceHoursServiceId] = useState("");
  const [serviceHours, setServiceHours] = useState<Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_off: boolean;
  }>>([]);
  const [serviceHoursLoading, setServiceHoursLoading] = useState(false);

  // Fetch user profile email and display name
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', user.id)
          .single();
        
        if (data?.email) {
          setBusinessEmail(data.email);
        }
        setDisplayName(user.user_metadata?.name || "");
      }
    };
    fetchProfile();
  }, [user]);

  // Load business hours from database
  useEffect(() => {
    const fetchBusinessHours = async () => {
      if (!business) return;

      try {
        const { data: hoursData, error } = await supabase
          .from("business_hours")
          .select("*")
          .eq("business_id", business.id)
          .is("location_id", null); // Get default hours (no location)

        if (error) throw error;

        if (hoursData && hoursData.length > 0) {
          // Map database hours to schedule state
          const newSchedule = DAYS.map((_, index) => {
            const dayHours = hoursData.find((h) => h.day_of_week === index);
            if (dayHours) {
              return {
                isOpen: !dayHours.is_closed,
                openTime: dayHours.open_time || "09:00",
                closeTime: dayHours.close_time || "17:00",
              };
            }
            return defaultSchedule[index];
          });
          setSchedule(newSchedule);
        }
      } catch (err) {
        console.error("Error loading business hours:", err);
      }
    };

    fetchBusinessHours();
  }, [business]);

  // Update form when business data loads - but don't clear if business is temporarily null during refetch
  useEffect(() => {
    if (business) {
      setCurrency(business.currency || "GHS");
      setBusinessName(business.name || "");
      setSupportBusinessName((prev) => prev || business.name || "");
      setDescription(business.description || "");
      setTimezone(business.timezone || "Africa/Accra");
      
      // Load payment settings (with proper defaults to avoid controlled/uncontrolled warning)
      const businessData = business as any;
      setPaymentMethod(businessData.payment_method ?? "");
      setPaymentBank(businessData.payment_bank ?? "");
      setPaymentAccountNumber(businessData.payment_account_number ?? "");
      setPaymentMobileMoneyNetwork(businessData.payment_mobile_money_network ?? "");
      setPaymentMobileMoneyNumber(businessData.payment_mobile_money_number ?? "");
      const loadedPaymentMethod = String(businessData.payment_method ?? "");
      if (loadedPaymentMethod === "momo") setPaymentRegion("ghana");
      if (loadedPaymentMethod === "mpesa") setPaymentRegion(business.currency === "ZAR" ? "south_africa" : "kenya");
      if (loadedPaymentMethod === "venmo" || loadedPaymentMethod === "cashapp") setPaymentRegion("united_states");
      setCollectBookingDeposits(businessData.collect_booking_deposits === true);
      setAllowFullPaymentAtBooking(businessData.allow_full_payment_at_booking === true);
      setBookingDepositPaymentInstructions(businessData.booking_deposit_payment_instructions ?? "");
      setFoodOrderPaymentInstructions(businessData.food_order_payment_instructions ?? "");

      const psEnabled = !!businessData.paystack_connected_account_enabled;
      const psMode = String(businessData.paystack_connected_account_mode || "").trim();
      const psLast4 = String(businessData.paystack_connected_account_last4 || "").trim();
      setBusinessPaystackEnabled(psEnabled);
      setBusinessPaystackMasked(
        psEnabled && psLast4
          ? `sk_${psMode || "live"}_••••${psLast4}`
          : "",
      );
      setBusinessPaystackSecretKey("");

      // Load business contact + socials
      const fullPhone: string = businessData.phone ?? "";
      const digits = cleanPhoneNumber(fullPhone, "233") || String(fullPhone || "").replace(/\D/g, "");
      const candidates = [
        { cc: "254", ui: "+254" },
        { cc: "234", ui: "+234" },
        { cc: "233", ui: "+233" },
        { cc: "44", ui: "+44" },
        { cc: "1", ui: "+1" },
      ];
      const hit = candidates.find((c) => digits.startsWith(c.cc));
      setPhoneCountryCode(hit?.ui || "+233");
      setBusinessPhoneLocal(hit ? digits.slice(hit.cc.length) : digits);
      setWhatsappUrl(businessData.whatsapp_url ?? "");
      setFacebookUrl(businessData.facebook_url ?? "");
      setTwitterUrl(businessData.twitter_url ?? "");
      setInstagramUrl(businessData.instagram_url ?? "");
      // Default labels; may be overwritten once extraSocialLinks loads (primary rows).
      setPrimarySocialLabels({
        instagram: "",
        facebook: "",
        twitter: "",
        linkedin: "",
        tiktok: "",
        whatsapp: "",
      });
      setLinkedinUrl(businessData.linkedin_url ?? "");
      setTiktokUrl(businessData.tiktok_url ?? "");
      
      // Load booking page customization
      setPrimaryColor(businessData.primary_color || "#2563EB");
      setCancellationPolicy(businessData.cancellation_policy || "");
      setTermsAndConditions(businessData.terms_and_conditions || "");
      setGoogleReviewUrl(businessData.google_review_url ?? "");
      setClientPhotosUrl(businessData.client_photos_url ?? "");
      setMultiServiceBookingEnabled(!!businessData.multi_service_booking_enabled);
      if (businessData.logo_url) {
        setLogoPreview(businessData.logo_url);
      } else {
        setLogoPreview("");
      }
      setLogoRemoved(false);

      // Header media (optional booking page hero carousel)
      const header = (businessData as any)?.booking_header_media_items;
      if (Array.isArray(header) && header.length > 0) {
        setHeaderMedia(
          header
            .map((it: any, idx: number) => {
              const url = String(it?.url || "").trim();
              if (!url) return null;
              const tRaw = String(it?.type || "").toLowerCase();
              const lower = url.toLowerCase();
              const type: "image" | "video" =
                tRaw === "video" || tRaw === "image"
                  ? (tRaw as any)
                  : lower.endsWith(".mp4") ||
                    lower.endsWith(".webm") ||
                    lower.endsWith(".mov") ||
                    lower.endsWith(".avi") ||
                    lower.endsWith(".mkv")
                  ? "video"
                  : "image";
              return {
                id: `header-existing:${businessData.id}:${idx}:${url}`,
                file: new File([], url.split("/").pop() || "existing-header-media"),
                preview: url,
                type,
              };
            })
            .filter(Boolean) as Array<{ id: string; file: File; preview: string; type: "image" | "video" }>,
        );
      } else {
        setHeaderMedia([]);
      }

      // Food vendor tools
      setOrdersEnabled(!!businessData.orders_enabled);
      setEventCateringRequestsEnabled(!!(businessData as any).event_catering_requests_enabled);
      setOrdersMultipleMenusEnabled(!!(businessData as any).orders_multiple_menus_enabled);
      setAccessDeskEnabled(businessData.access_desk_enabled !== false);
      
      // Appointments invoicing
      setAppointmentsInvoicingEnabled(!!businessData.appointments_invoicing_enabled);
      
      // Log book
      setLogBookEnabled(!!businessData.log_book_enabled);
      setSalesEnabled(!!(businessData as any).sales_enabled);
      setSmsRemindersEnabled((businessData as any).sms_reminders_enabled !== false);
      const theme = (businessData as any).accent_theme;
      setAccentTheme(ACCENT_THEMES.includes(theme as AccentTheme) ? (theme as AccentTheme) : theme === "emerald" ? "lavender" : "blue");
    }
  }, [business]);

  useEffect(() => {
    if (paymentRegionManuallyChanged) return;
    const defaultLocation =
      locations.find((location) => (location as Tables<"locations"> & { is_default?: boolean | null }).is_default) ||
      locations[0];
    const detectedCountry = String(
      (defaultLocation as (Tables<"locations"> & { country?: string | null }) | undefined)?.country ||
        locationCountry ||
        "",
    );
    const inferred = inferPaymentRegion(detectedCountry, currency);
    setPaymentRegion(inferred);
    setPaymentMethod((current) => {
      const available = REGION_METHODS[inferred];
      return current && available.includes(current as DirectPaymentMethod) ? current : available[0];
    });
  }, [locations, locationCountry, currency, paymentRegionManuallyChanged]);

  // Load additional socials/logos (optional)
  useEffect(() => {
    const run = async () => {
      if (!business?.id) return;
      try {
        const [linksRes, logosRes] = await Promise.all([
          supabase
            .from("business_social_links" as any)
            .select("id, platform, label, url, position, active, is_primary")
            .eq("business_id", business.id)
            .eq("active", true)
            .order("platform", { ascending: true })
            .order("is_primary", { ascending: false })
            .order("position", { ascending: true }),
          supabase
            .from("business_logos" as any)
            .select("id, label, url, position, active")
            .eq("business_id", business.id)
            .eq("active", true)
            .order("position", { ascending: true }),
        ]);
        const links = (linksRes as any)?.data || [];
        setExtraSocialLinks(links);
        const primaryMap: any = {
          instagram: "",
          facebook: "",
          twitter: "",
          linkedin: "",
          tiktok: "",
          whatsapp: "",
        };
        for (const l of links || []) {
          const p = String(l?.platform || "");
          if (!l?.is_primary) continue;
          if (primaryMap[p] === undefined) continue;
          primaryMap[p] = String(l?.label || "").trim();
        }
        setPrimarySocialLabels(primaryMap);
        setExtraLogos((logosRes as any)?.data || []);
      } catch {
        setExtraSocialLinks([]);
        setExtraLogos([]);
      }
    };
    run();
  }, [business?.id]);

  const refreshExtraBranding = async () => {
    if (!business?.id) return;
    const [linksRes, logosRes] = await Promise.all([
      supabase
        .from("business_social_links" as any)
        .select("id, platform, label, url, position, active, is_primary")
        .eq("business_id", business.id)
        .eq("active", true)
        .order("platform", { ascending: true })
        .order("is_primary", { ascending: false })
        .order("position", { ascending: true }),
      supabase
        .from("business_logos" as any)
        .select("id, label, url, position, active")
        .eq("business_id", business.id)
        .eq("active", true)
        .order("position", { ascending: true }),
    ]);
    const links = (linksRes as any)?.data || [];
    setExtraSocialLinks(links);
    const primaryMap: any = {
      instagram: "",
      facebook: "",
      twitter: "",
      linkedin: "",
      tiktok: "",
      whatsapp: "",
    };
    for (const l of links || []) {
      const p = String(l?.platform || "");
      if (!l?.is_primary) continue;
      if (primaryMap[p] === undefined) continue;
      primaryMap[p] = String(l?.label || "").trim();
    }
    setPrimarySocialLabels(primaryMap);
    setExtraLogos((logosRes as any)?.data || []);
  };

  const primarySocialPlatforms = SOCIAL_PLATFORM_OPTIONS.filter(
    (option): option is (typeof SOCIAL_PLATFORM_OPTIONS)[number] & { platform: AddableSocialPlatform } =>
      option.platform !== "whatsapp",
  );

  const getPrimarySocialUrl = (platform: AddableSocialPlatform) => {
    switch (platform) {
      case "facebook":
        return facebookUrl;
      case "twitter":
        return twitterUrl;
      case "instagram":
        return instagramUrl;
      case "linkedin":
        return linkedinUrl;
      case "tiktok":
        return tiktokUrl;
      default:
        return "";
    }
  };

  const setPrimarySocialUrlByPlatform = (platform: AddableSocialPlatform, value: string) => {
    switch (platform) {
      case "facebook":
        setFacebookUrl(value);
        break;
      case "twitter":
        setTwitterUrl(value);
        break;
      case "instagram":
        setInstagramUrl(value);
        break;
      case "linkedin":
        setLinkedinUrl(value);
        break;
      case "tiktok":
        setTiktokUrl(value);
        break;
    }
  };

  const setPrimarySocialLabelByPlatform = (platform: AddableSocialPlatform, value: string) => {
    setPrimarySocialLabels((prev) => ({ ...prev, [platform]: value }));
  };

  const openAddPrimarySocialDialog = () => {
    const nextPlatform =
      primarySocialPlatforms.find((option) => !String(getPrimarySocialUrl(option.platform) || "").trim())?.platform ||
      "instagram";
    setPrimarySocialPlatform(nextPlatform);
    setPrimarySocialLabelDraft("");
    setPrimarySocialUrlDraft("");
    setPrimarySocialDialogOpen(true);
  };

  const handleAddPrimarySocial = () => {
    const url = String(primarySocialUrlDraft || "").trim();
    if (!url) {
      toast({ title: "Missing URL", description: "Please enter a link URL.", variant: "destructive" });
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a full URL starting with http:// or https://",
        variant: "destructive",
      });
      return;
    }
    setPrimarySocialUrlByPlatform(primarySocialPlatform, url);
    setPrimarySocialLabelByPlatform(primarySocialPlatform, primarySocialLabelDraft.trim());
    setPrimarySocialDialogOpen(false);
    setPrimarySocialLabelDraft("");
    setPrimarySocialUrlDraft("");
  };

  const handleRemovePrimarySocial = (platform: AddableSocialPlatform) => {
    setPrimarySocialUrlByPlatform(platform, "");
    setPrimarySocialLabelByPlatform(platform, "");
  };

  const fetchCoupons = async () => {
    if (!business?.id) return;
    setCouponsLoading(true);
    try {
      const { data, error } = await supabase
        .from("business_coupons" as any)
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const rows = (data as any[]) || [];
      setCoupons(rows);

      // Fetch service scoping for these coupons (optional; table may not exist yet)
      const ids = rows.map((r) => String(r.id)).filter(Boolean);
      if (ids.length === 0) {
        setCouponServiceIdsByCouponId({});
        setCouponCategoryIdsByCouponId({});
      } else {
        try {
          const { data: mapRows } = await supabase
            .from("business_coupon_services" as any)
            .select("coupon_id,service_id")
            .in("coupon_id", ids);
          const next: Record<string, string[]> = {};
          for (const mr of (mapRows as any[]) || []) {
            const cid = String(mr.coupon_id || "");
            const sid = String(mr.service_id || "");
            if (!cid || !sid) continue;
            if (!next[cid]) next[cid] = [];
            next[cid].push(sid);
          }
          setCouponServiceIdsByCouponId(next);
        } catch {
          setCouponServiceIdsByCouponId({});
        }

        // Fetch category scoping (optional; table may not exist yet)
        try {
          const { data: catRows } = await supabase
            .from("business_coupon_categories" as any)
            .select("coupon_id,category_id")
            .in("coupon_id", ids);
          const nextCats: Record<string, string[]> = {};
          for (const mr of (catRows as any[]) || []) {
            const cid = String(mr.coupon_id || "");
            const catId = String(mr.category_id || "");
            if (!cid || !catId) continue;
            if (!nextCats[cid]) nextCats[cid] = [];
            nextCats[cid].push(catId);
          }
          setCouponCategoryIdsByCouponId(nextCats);
        } catch {
          setCouponCategoryIdsByCouponId({});
        }
      }

      // Fetch usage counts (optional; RPC added via migration)
      try {
        const { data: usage } = await (supabase as any).rpc("get_coupon_usage_counts", { p_business_id: business.id });
        const nextUses: Record<string, { total: number; active: number; completed: number }> = {};
        for (const u of (usage || []) as any[]) {
          const cid = String(u.coupon_id || "");
          if (!cid) continue;
          nextUses[cid] = {
            total: Number(u.uses_total || 0),
            active: Number(u.uses_active || 0),
            completed: Number(u.uses_completed || 0),
          };
        }
        setCouponUsesByCouponId(nextUses);
      } catch {
        setCouponUsesByCouponId({});
      }
    } catch (err: any) {
      // Table missing (migration not run) should not break Settings
      if (err?.code === "PGRST204" || String(err?.message || "").includes("Could not find")) {
        setCoupons([]);
        setCouponServiceIdsByCouponId({});
        setCouponCategoryIdsByCouponId({});
        setCouponUsesByCouponId({});
      } else {
        console.error("Failed to load coupons:", err);
        setCoupons([]);
        setCouponServiceIdsByCouponId({});
        setCouponCategoryIdsByCouponId({});
        setCouponUsesByCouponId({});
      }
    } finally {
      setCouponsLoading(false);
    }
  };

  useEffect(() => {
    void fetchCoupons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  // Load default location (address)
  useEffect(() => {
    const fetchDefaultLocation = async () => {
      if (!business) return;
      try {
        // Prefer business.location_id (primary location), fallback to default location row
        const base = supabase.from("locations").select("*").eq("business_id", business.id);
        const query = business.location_id
          ? base.eq("id", business.location_id).maybeSingle()
          : base.order("is_default", { ascending: false }).limit(1).maybeSingle();

        const { data, error } = await query;
        if (error) throw error;
        setDefaultLocationId(data?.id ?? null);
        setLocationAddress(data?.address ?? "");
        setLocationCity((data as any)?.city ?? "");
        setLocationCountry((data as any)?.country ?? "");
        setLocationPlaceId((data as any)?.place_id ?? "");
        setLocationLat(typeof (data as any)?.latitude === "number" ? (data as any).latitude : null);
        setLocationLng(typeof (data as any)?.longitude === "number" ? (data as any).longitude : null);
      } catch (err) {
        console.error("Error loading location:", err);
      }
    };
    fetchDefaultLocation();
  }, [business]);

  // Cleanup logo preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList | null } }) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file type",
        description: "Please upload an image file (PNG, JPG, etc.)",
        variant: "destructive",
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setLogoFile(file);
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
    setLogoRemoved(false);
  };

  const handleHeaderMediaSelect = (
    e: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList | null } },
  ) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newMedia: Array<{ id: string; file: File; preview: string; type: "image" | "video" }> = [];
    Array.from(files).forEach((file) => {
      const isVideo = file.type.startsWith("video/");
      const isImage = file.type.startsWith("image/");
      if (!isVideo && !isImage) return;
      if (file.size > 25 * 1024 * 1024) return; // 25MB cap

      const preview = URL.createObjectURL(file);
      newMedia.push({
        id: `header-new:${Date.now()}:${Math.random().toString(16).slice(2)}`,
        file,
        preview,
        type: isVideo ? "video" : "image",
      });
    });

    setHeaderMedia((prev) => {
      const merged = [...prev, ...newMedia];
      return merged.slice(0, 6);
    });

    if (headerMediaInputRef.current) headerMediaInputRef.current.value = "";
  };

  const removeHeaderMedia = (idx: number) => {
    setHeaderMedia((prev) => {
      if (idx < 0 || idx >= prev.length) return prev;
      const next = [...prev];
      try {
        if (next[idx].preview.startsWith("blob:")) URL.revokeObjectURL(next[idx].preview);
      } catch {
        // ignore
      }
      next.splice(idx, 1);
      return next;
    });
  };

  const moveHeaderMedia = (from: number, to: number) => {
    setHeaderMedia((prev) => {
      if (from === to) return prev;
      if (from < 0 || to < 0) return prev;
      if (from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handleSaveBookingSettings = async () => {
    if (!business) {
      toast({
        title: "Error",
        description: "No business found",
        variant: "destructive",
      });
      return;
    }

    setIsSavingBooking(true);
    try {
      let logoUrl: string | undefined = undefined;
      let headerItems: Array<{ url: string; type: "image" | "video" }> = [];

      // Upload logo if a new file was selected
      if (logoFile && business) {
        const fileExt = logoFile.name.split(".").pop();
        const fileName = `logos/${business.id}/${Date.now()}.${fileExt}`;

        // First, delete old logo if it exists
        if (business.logo_url) {
          const oldFileName = business.logo_url.split("/").slice(-2).join("/");
          try {
            await supabase.storage.from("service-media").remove([oldFileName]);
          } catch (err) {
            // Ignore errors when deleting old logo
            console.log("Could not delete old logo:", err);
          }
        }

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("service-media")
          .upload(fileName, logoFile, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          toast({
            title: "Upload failed",
            description: "Failed to upload logo. Other settings will be saved.",
            variant: "destructive",
          });
        } else {
          // Get public URL
          const { data: urlData } = supabase.storage
            .from("service-media")
            .getPublicUrl(uploadData.path);

          logoUrl = urlData.publicUrl;
        }
      } else if (!logoRemoved && logoPreview && !logoFile) {
        // Keep existing logo if no new file was selected
        logoUrl = logoPreview;
      }

      // Upload new header media (keep existing URLs), cap to 6 items
      try {
        const drafts = (headerMedia || []).slice(0, 6);
        if (drafts.length === 0) {
          headerItems = [];
        } else {
          const processed = await Promise.all(
            drafts.map(async (m) => {
              const isExisting =
                m.preview.startsWith("http://") || m.preview.startsWith("https://");
              if (isExisting) return { url: m.preview, type: m.type };

              const fileExt =
                m.file.name.split(".").pop() || (m.type === "video" ? "mp4" : "jpg");
              const fileName = `booking-header/${business.id}/${Date.now()}-${Math.random()
                .toString(36)
                .substring(7)}.${fileExt}`;

              const { data: uploadData, error: uploadError } = await supabase.storage
                .from("service-media")
                .upload(fileName, m.file, {
                  cacheControl: "3600",
                  upsert: false,
                  contentType: m.file.type || undefined,
                });

              if (uploadError) throw uploadError;

              const { data: urlData } = supabase.storage
                .from("service-media")
                .getPublicUrl(uploadData.path);

              return { url: urlData.publicUrl, type: m.type };
            }),
          );
          headerItems = processed.filter((x) => !!x?.url);
        }
      } catch (err) {
        console.error("Header media upload error:", err);
        toast({
          title: "Header media upload failed",
          description: "Some header media couldn't be uploaded. Other settings will be saved.",
          variant: "destructive",
        });
        // Best-effort: keep existing URLs only
        headerItems = (headerMedia || [])
          .filter((m) => m.preview.startsWith("http://") || m.preview.startsWith("https://"))
          .slice(0, 6)
          .map((m) => ({ url: m.preview, type: m.type }));
      }

      // Validate color format
      if (!/^#[0-9A-Fa-f]{6}$/.test(primaryColor)) {
        toast({
          title: "Invalid color",
          description: "Please enter a valid hex color code (e.g., #2563EB)",
          variant: "destructive",
        });
        setIsSavingBooking(false);
        return;
      }

      const googleUrl = String(googleReviewUrl || "").trim();
      if (googleUrl && !/^https?:\/\//i.test(googleUrl)) {
        toast({
          title: "Invalid Google review link",
          description: "Please enter a full URL starting with http:// or https://",
          variant: "destructive",
        });
        setIsSavingBooking(false);
        return;
      }

      const photosUrl = String(clientPhotosUrl || "").trim();
      if (photosUrl && !/^https?:\/\//i.test(photosUrl)) {
        toast({
          title: "Invalid photos link",
          description: "Please enter a full URL starting with http:// or https://",
          variant: "destructive",
        });
        setIsSavingBooking(false);
        return;
      }

      const updateData: any = {
        primary_color: primaryColor,
        cancellation_policy: cancellationPolicy || null,
        terms_and_conditions: termsAndConditions || null,
        google_review_url: googleUrl || null,
        client_photos_url: photosUrl || null,
        multi_service_booking_enabled: !!multiServiceBookingEnabled,
        booking_header_media_items: headerItems,
      };

      if (logoRemoved) {
        updateData.logo_url = null;

        // Best-effort: delete old logo file (if it looks like it belongs to our storage paths)
        if (business.logo_url) {
          const oldFileName = business.logo_url.split("/").slice(-2).join("/");
          try {
            await supabase.storage.from("service-media").remove([oldFileName]);
          } catch {
            // ignore
          }
        }
      } else if (logoUrl !== undefined) {
        updateData.logo_url = logoUrl;
      }

      const { error } = await supabase
        .from("businesses")
        .update(updateData)
        .eq("id", business.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Booking page settings saved successfully",
      });

      await refetch();
    } catch (err: any) {
      console.error("Error saving booking settings:", err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to save booking settings",
        variant: "destructive",
      });
    } finally {
      setIsSavingBooking(false);
    }
  };

  const handleAddExtraSocial = async () => {
    if (!business?.id) return;
    const url = String(socialUrlNew || "").trim();
    if (!url) {
      toast({ title: "Missing URL", description: "Please enter a link URL.", variant: "destructive" });
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast({
        title: "Invalid URL",
        description: "Please enter a full URL starting with http:// or https://",
        variant: "destructive",
      });
      return;
    }
    setIsSavingSocial(true);
    try {
      const platformRows = (extraSocialLinks || []).filter(
        (r: any) => String(r.platform) === socialPlatform && !r?.is_primary,
      );
      const nextPos = platformRows.length ? Math.max(...platformRows.map((r: any) => Number(r.position || 0))) + 1 : 0;
      const { error } = await supabase
        .from("business_social_links" as any)
        .insert({
          business_id: business.id,
          platform: socialPlatform,
          label: socialLabel.trim() || null,
          url,
          position: nextPos,
          active: true,
        } as any);
      if (error) throw error;
      toast({ title: "Added", description: "Social account added." });
      setSocialLabel("");
      setSocialUrlNew("");
      setSocialDialogOpen(false);
      await refreshExtraBranding();
    } catch (e: any) {
      toast({ title: "Error", description: e?.message || "Failed to add social account.", variant: "destructive" });
    } finally {
      setIsSavingSocial(false);
    }
  };

  const handleRemoveExtraSocial = async (id: string) => {
    if (!business?.id) return;
    try {
      const { error } = await supabase.from("business_social_links" as any).delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Removed", description: "Social account removed." });
      await refreshExtraBranding();
    } catch (e: any) {
      toast({ title: "Error", description: e?.message || "Failed to remove social account.", variant: "destructive" });
    }
  };

  const handleAddExtraLogo = async (file: File) => {
    if (!business?.id) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please upload an image file.", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please upload an image smaller than 5MB.", variant: "destructive" });
      return;
    }

    setIsSavingExtraLogo(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `logos/${business.id}/extra-${Date.now()}.${fileExt}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("service-media")
        .upload(fileName, file, { cacheControl: "3600", upsert: false });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("service-media").getPublicUrl(uploadData.path);
      const url = urlData.publicUrl;

      const nextPos = extraLogos.length ? Math.max(...extraLogos.map((r: any) => Number(r.position || 0))) + 1 : 0;
      const { error } = await supabase
        .from("business_logos" as any)
        .insert({ business_id: business.id, url, label: null, position: nextPos, active: true } as any);
      if (error) throw error;

      toast({ title: "Added", description: "Additional logo added." });
      await refreshExtraBranding();
    } catch (e: any) {
      toast({ title: "Error", description: e?.message || "Failed to add logo.", variant: "destructive" });
    } finally {
      setIsSavingExtraLogo(false);
      setExtraLogoFile(null);
      if (extraLogoInputRef.current) extraLogoInputRef.current.value = "";
    }
  };

  const handleRemoveExtraLogo = async (logo: any) => {
    if (!business?.id) return;
    const id = String(logo?.id || "");
    const url = String(logo?.url || "");
    if (!id) return;
    try {
      const { error } = await supabase.from("business_logos" as any).delete().eq("id", id);
      if (error) throw error;

      // Best-effort: delete from storage if it looks like a Supabase public URL for our bucket.
      const marker = "/storage/v1/object/public/service-media/";
      const idx = url.indexOf(marker);
      if (idx >= 0) {
        const path = url.slice(idx + marker.length).split("?")[0];
        if (path) {
          try {
            await supabase.storage.from("service-media").remove([path]);
          } catch {
            // ignore
          }
        }
      }

      toast({ title: "Removed", description: "Logo removed." });
      await refreshExtraBranding();
    } catch (e: any) {
      toast({ title: "Error", description: e?.message || "Failed to remove logo.", variant: "destructive" });
    }
  };

  // Location management functions
  const resetLocationForm = () => {
    setEditingLocationId(null);
    setLocationFormName("");
    setLocationFormAddress("");
    setLocationFormCity("");
    setLocationFormCountry("");
    setLocationFormPlaceId("");
    setLocationFormLat(null);
    setLocationFormLng(null);
  };

  const handleOpenLocationDialog = (location?: typeof locations[0]) => {
    if (location) {
      setEditingLocationId(location.id);
      setLocationFormName(location.name || "");
      setLocationFormAddress(location.address || "");
      setLocationFormCity((location as any).city || "");
      setLocationFormCountry((location as any).country || "");
      setLocationFormPlaceId((location as any).place_id || "");
      setLocationFormLat(typeof (location as any).latitude === "number" ? (location as any).latitude : null);
      setLocationFormLng(typeof (location as any).longitude === "number" ? (location as any).longitude : null);
    } else {
      resetLocationForm();
    }
    setLocationDialogOpen(true);
  };

  const handleSaveLocation = async () => {
    if (!business) return;
    if (!locationFormName.trim() || !locationFormAddress.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name and address for the location",
        variant: "destructive",
      });
      return;
    }

    // Check location limit for starter plan
    if (!editingLocationId) {
      const planId = (business as any)?.plan_id || "starter";
      const locationLimit = currentPlan?.location_limit;
      
      if (planId === "starter" && locationLimit !== null && locations.length >= locationLimit) {
        toast({
          title: "Location Limit Reached",
          description: isNativeIos
            ? "Please contact Buukd support for help adding more locations."
            : "Starter plan allows only 1 location. Upgrade to Professional plan to add more locations.",
          variant: "destructive",
        });
        return;
      }
      
      // Check limit for other plans too
      if (locationLimit !== null && locations.length >= locationLimit) {
        toast({
          title: "Location Limit Reached",
          description: isNativeIos
            ? "Please contact Buukd support for help adding more locations."
            : `You've reached your plan's location limit (${locationLimit}). Upgrade your plan to add more locations.`,
          variant: "destructive",
        });
        return;
      }
    }

    setIsSavingLocation(true);
    try {
      let latToSave = locationFormLat;
      let lngToSave = locationFormLng;
      if (latToSave == null || lngToSave == null) {
        const fallback = await geocodeAddressFallback(locationFormAddress.trim());
        if (fallback) {
          latToSave = fallback.lat;
          lngToSave = fallback.lng;
        }
      }

      const locationData: any = {
        business_id: business.id,
        name: locationFormName.trim(),
        address: locationFormAddress.trim(),
        city: locationFormCity || null,
        country: locationFormCountry || null,
        place_id: locationFormPlaceId || null,
        latitude: latToSave,
        longitude: lngToSave,
      };

      if (editingLocationId) {
        const { error } = await supabase
          .from("locations")
          .update(locationData)
          .eq("id", editingLocationId);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Location updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("locations")
          .insert(locationData);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Location added successfully",
        });
      }

      await refetchLocations();
      setLocationDialogOpen(false);
      resetLocationForm();
    } catch (error) {
      console.error("Error saving location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save location",
        variant: "destructive",
      });
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!confirm("Are you sure you want to delete this location? This action cannot be undone.")) {
      return;
    }

    try {
      const { error } = await supabase
        .from("locations")
        .delete()
        .eq("id", locationId);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Location deleted successfully",
      });
      await refetchLocations();
    } catch (error) {
      console.error("Error deleting location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete location",
        variant: "destructive",
      });
    }
  };

  const handleSetDefaultLocation = async (locationId: string) => {
    if (!business) return;

    try {
      // First, unset all default locations
      await supabase
        .from("locations")
        .update({ is_default: false } as any)
        .eq("business_id", business.id);

      // Set the selected location as default
      const { error } = await supabase
        .from("locations")
        .update({ is_default: true } as any)
        .eq("id", locationId);
      if (error) throw error;

      // Update business primary location
      await supabase
        .from("businesses")
        .update({ location_id: locationId } as any)
        .eq("id", business.id);

      toast({
        title: "Success",
        description: "Default location updated",
      });
      await refetchLocations();
      await refetch();
    } catch (error) {
      console.error("Error setting default location:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to set default location",
        variant: "destructive",
      });
    }
  };

  const resetBusinessTabState = async () => {
    if (!business) return;
    const businessData = business as any;
    setCurrency(business.currency || "GHS");
    setDescription(business.description || "");
    setTimezone(business.timezone || "Africa/Accra");
    setDisplayName(user?.user_metadata?.name || "");
    setOrdersEnabled(!!businessData.orders_enabled);
    setEventCateringRequestsEnabled(!!businessData.event_catering_requests_enabled);
    setOrdersMultipleMenusEnabled(!!businessData.orders_multiple_menus_enabled);
    setAccessDeskEnabled(businessData.access_desk_enabled !== false);
    setAppointmentsInvoicingEnabled(!!businessData.appointments_invoicing_enabled);
    setLogBookEnabled(!!businessData.log_book_enabled);
    setSalesEnabled(!!businessData.sales_enabled);
    setSmsRemindersEnabled(businessData.sms_reminders_enabled !== false);
    const theme = businessData.accent_theme;
    setAccentTheme(ACCENT_THEMES.includes(theme as AccentTheme) ? (theme as AccentTheme) : theme === "emerald" ? "lavender" : "blue");

    const fullPhone: string = businessData.phone ?? "";
    const digits = cleanPhoneNumber(fullPhone, "233") || String(fullPhone || "").replace(/\D/g, "");
    const candidates = [
      { cc: "254", ui: "+254" },
      { cc: "234", ui: "+234" },
      { cc: "233", ui: "+233" },
      { cc: "44", ui: "+44" },
      { cc: "1", ui: "+1" },
    ];
    const hit = candidates.find((c) => digits.startsWith(c.cc));
    setPhoneCountryCode(hit?.ui || "+233");
    setBusinessPhoneLocal(hit ? digits.slice(hit.cc.length) : digits);
    setWhatsappUrl(businessData.whatsapp_url ?? "");
    setFacebookUrl(businessData.facebook_url ?? "");
    setTwitterUrl(businessData.twitter_url ?? "");
    setInstagramUrl(businessData.instagram_url ?? "");
    setLinkedinUrl(businessData.linkedin_url ?? "");
    setTiktokUrl(businessData.tiktok_url ?? "");
    setShowExtraLinksSection(false);
    setPrimarySocialDialogOpen(false);
    setPrimarySocialLabelDraft("");
    setPrimarySocialUrlDraft("");
    setSocialDialogOpen(false);
    setSocialPlatform("instagram");
    setSocialLabel("");
    setSocialUrlNew("");

    await refreshExtraBranding();

    try {
      const base = supabase.from("locations").select("*").eq("business_id", business.id);
      const query = business.location_id
        ? base.eq("id", business.location_id).maybeSingle()
        : base.order("is_default", { ascending: false }).limit(1).maybeSingle();
      const { data, error } = await query;
      if (error) throw error;
      setDefaultLocationId(data?.id ?? null);
      setLocationAddress(data?.address ?? "");
      setLocationCity((data as any)?.city ?? "");
      setLocationCountry((data as any)?.country ?? "");
      setLocationPlaceId((data as any)?.place_id ?? "");
      setLocationLat(typeof (data as any)?.latitude === "number" ? (data as any).latitude : null);
      setLocationLng(typeof (data as any)?.longitude === "number" ? (data as any).longitude : null);
    } catch (err) {
      console.error("Error resetting location:", err);
    }
  };

  const handleSaveSettings = async () => {
    if (!business) {
      toast({
        title: "Error",
        description: "No business found. Please reload the page or contact support.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const normalizedDisplayName = String(displayName || "").trim();
      const currentDisplayName = String(user?.user_metadata?.name || "").trim();
      if (normalizedDisplayName && normalizedDisplayName !== currentDisplayName) {
        const { error: authError } = await supabase.auth.updateUser({ data: { name: normalizedDisplayName } });
        if (authError) throw authError;
      }

      // Normalize phone to E.164-ish digits (e.g. GH: 024.. -> 23324..). Idempotent: avoids 2330... and 233233...
      const rawLocal = (businessPhoneLocal || "").trim();
      const selectedCC = String(phoneCountryCode || "+233").replace("+", "");
      const normalizedPhone = rawLocal ? phoneToE164(`${phoneCountryCode}${rawLocal}`, selectedCC) : null;

      // Prepare update data - only include fields that have values
      const updateData: any = {
        currency,
        timezone,
        phone: normalizedPhone,
        whatsapp_url: whatsappUrl || null,
        facebook_url: facebookUrl || null,
        twitter_url: twitterUrl || null,
        instagram_url: instagramUrl || null,
        linkedin_url: linkedinUrl || null,
        tiktok_url: tiktokUrl || null,
      };

      // Membership module-only feature flags
      if (primaryModule === "memberships") {
        updateData.orders_enabled = !!ordersEnabled;
        updateData.access_desk_enabled = !!accessDeskEnabled;
      }

      // Catering Services: Orders is the core workflow, keep it enabled
      if (primaryModule === "catering_services" || primaryModule === "food_vendors") {
        updateData.orders_enabled = true;
        updateData.event_catering_requests_enabled = !!eventCateringRequestsEnabled;
        (updateData as any).orders_multiple_menus_enabled = !!ordersMultipleMenusEnabled;
      }

      // Appointments module: invoicing feature flag
      if (primaryModule === "appointments") {
        updateData.appointments_invoicing_enabled = !!appointmentsInvoicingEnabled;
      }
      
      // Appointments/Clinics module: log book + notification flags + sales
      if (primaryModule === "appointments" || primaryModule === "clinics") {
        updateData.log_book_enabled = !!logBookEnabled;
        (updateData as any).sales_enabled = !!salesEnabled;
        (updateData as any).sms_reminders_enabled = !!smsRemindersEnabled;
      }
      
      // Only add description if it has a value
      if (description !== undefined && description !== null) {
        updateData.description = description;
      }

      const validAccentThemes = ["blue", "pink", "purple", "lavender", "teal", "navy", "charcoal"] as const;
      (updateData as any).accent_theme = validAccentThemes.includes(String(accentTheme || "") as any) ? String(accentTheme) : "blue";

      const { error } = await supabase
        .from("businesses")
        .update(updateData)
        .eq("id", business.id)
        .select()
        .single();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      // Persist primary labels (optional) for the public dropdown chooser.
      // We store them as a single is_primary row per platform in business_social_links so the public pages can read them.
      try {
        const rows: any[] = [];
        const mapping: Array<{ platform: any; url: string; label: string }> = [
          { platform: "instagram", url: String(instagramUrl || "").trim(), label: String(primarySocialLabels.instagram || "").trim() },
          { platform: "facebook", url: String(facebookUrl || "").trim(), label: String(primarySocialLabels.facebook || "").trim() },
          { platform: "twitter", url: String(twitterUrl || "").trim(), label: String(primarySocialLabels.twitter || "").trim() },
          { platform: "linkedin", url: String(linkedinUrl || "").trim(), label: String(primarySocialLabels.linkedin || "").trim() },
          { platform: "tiktok", url: String(tiktokUrl || "").trim(), label: String(primarySocialLabels.tiktok || "").trim() },
          { platform: "whatsapp", url: String(whatsappUrl || "").trim(), label: String(primarySocialLabels.whatsapp || "").trim() },
        ];

        // Clear existing primary rows for all supported platforms.
        await supabase
          .from("business_social_links" as any)
          .delete()
          .eq("business_id", business.id)
          .eq("is_primary", true);

        for (const m of mapping) {
          if (!m.url) continue;
          rows.push({
            business_id: business.id,
            platform: m.platform,
            label: m.label || null,
            url: m.url,
            position: 0,
            active: true,
            is_primary: true,
          });
        }
        if (rows.length) {
          await supabase.from("business_social_links" as any).insert(rows as any);
        }

        // Keep local state in sync with DB (labels are loaded from the primary rows).
        await refreshExtraBranding();
      } catch (e) {
        // Non-blocking: business settings are already saved.
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("[Settings] Failed to persist primary social labels:", e);
        }
      }

      toast({
        title: "Settings saved",
        description: "Your business settings have been updated successfully.",
      });

      // Upsert default location address (optional)
      const addr = locationAddress.trim();
      if (addr) {
        let latToSave = locationLat;
        let lngToSave = locationLng;
        if (latToSave == null || lngToSave == null) {
          const fallback = await geocodeAddressFallback(addr);
          if (fallback) {
            latToSave = fallback.lat;
            lngToSave = fallback.lng;
          }
        }

        if (defaultLocationId) {
          const { error: locErr } = await supabase
            .from("locations")
            .update({
              address: addr,
              city: locationCity || null,
              country: locationCountry || null,
              place_id: locationPlaceId || null,
              latitude: latToSave,
              longitude: lngToSave,
              is_default: true,
            } as any)
            .eq("id", defaultLocationId);
          if (locErr) throw locErr;

          // Set business primary location
          const { error: bizLocErr } = await supabase
            .from("businesses")
            .update({ location_id: defaultLocationId } as any)
            .eq("id", business.id);
          if (bizLocErr) throw bizLocErr;
        } else {
          const { data: locData, error: locErr } = await supabase
            .from("locations")
            .insert({
              business_id: business.id,
              name: "Main Location",
              address: addr,
              city: locationCity || null,
              country: locationCountry || null,
              place_id: locationPlaceId || null,
              latitude: latToSave,
              longitude: lngToSave,
              is_default: true,
            } as any)
            .select()
            .single();
          if (locErr) throw locErr;
          setDefaultLocationId(locData?.id ?? null);

          if (locData?.id) {
            const { error: bizLocErr } = await supabase
              .from("businesses")
              .update({ location_id: locData.id } as any)
              .eq("id", business.id);
            if (bizLocErr) throw bizLocErr;
          }
        }
      } else if (defaultLocationId) {
        const { error: locErr } = await supabase
          .from("locations")
          .update({ address: null, place_id: null, latitude: null, longitude: null } as any)
          .eq("id", defaultLocationId);
        if (locErr) throw locErr;

        // Clear primary location on business if user removed address
        const { error: bizLocErr } = await supabase
          .from("businesses")
          .update({ location_id: null } as any)
          .eq("id", business.id);
        if (bizLocErr) throw bizLocErr;
      }
      
      // Refetch to get the latest data
      await refetch();
    } catch (error) {
      console.error("Save settings error:", error);
      const errAny: any = error;
      const isSchemaCache =
        errAny?.code === "PGRST204" ||
        (typeof errAny?.message === "string" && errAny.message.includes("schema cache"));
      const mentionsLocationId =
        typeof errAny?.message === "string" && errAny.message.includes("location_id");

      if (isSchemaCache && mentionsLocationId) {
        toast({
          title: "Database migration required",
          description:
            "Your database is missing businesses.location_id. Run migration 20260107061000_add_business_location_id.sql, then reload the API schema and refresh this page.",
          variant: "destructive",
        });
      } else {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save settings",
        variant: "destructive",
      });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Staff management functions
  const resetStaffForm = () => {
    setFormName("");
    setFormEmail("");
    setFormPhone("");
    setFormRole("staff");
    setEditingStaffId(null);
  };

  const handleEditStaff = (member: typeof staff[0]) => {
    setEditingStaffId(member.id);
    setFormName(member.name);
    setFormEmail(member.email || "");
    setFormPhone(member.phone || "");
    setFormRole(member.role);
    setIsStaffDialogOpen(true);
  };

  const handleSaveStaff = async () => {
    if (!formName.trim()) {
      toast({
        title: "Error",
        description: "Staff name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const normalizedPhone = formPhone.trim()
        ? phoneToE164(formPhone.trim(), "233") || formPhone.trim()
        : null;
      const staffPayload: Record<string, unknown> = {
        name: formName.trim(),
        email: formEmail.trim() || null,
        phone: normalizedPhone,
        role: formRole,
        portal_enabled: false,
        portal_role: null,
      };

      if (editingStaffId) {
        await updateStaff(editingStaffId, {
          name: staffPayload.name as string,
          email: staffPayload.email as string | null,
          phone: staffPayload.phone as string | null,
          role: formRole,
        });
      } else {
        await createStaff(staffPayload as any, []);
      }
      setIsStaffDialogOpen(false);
      resetStaffForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save staff member",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteStaff = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to remove ${name}? This will deactivate their account.`)) {
      return;
    }

    try {
      await deleteStaff(id);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to remove staff member",
        variant: "destructive",
      });
    }
  };

  // Staff Hours Management
  const handleManageHours = async (member: typeof staff[0]) => {
    setManagingHoursStaffId(member.id);
    
    // Fetch existing staff hours
    const { data: existingHours, error } = await supabase
      .from("staff_hours")
      .select("*")
      .eq("staff_id", member.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load staff hours",
        variant: "destructive",
      });
      return;
    }

    // Initialize hours for all days
    const hours = DAYS.map((_, index) => {
      const existing = existingHours?.find(h => h.day_of_week === index);
      return {
        day_of_week: index,
        start_time: existing?.start_time || "09:00",
        end_time: existing?.end_time || "17:00",
        is_off: existing?.is_off ?? false,
      };
    });

    setStaffHours(hours);
    setIsHoursDialogOpen(true);
  };

  const handleSaveStaffHours = async () => {
    if (!managingHoursStaffId) return;

    setIsSaving(true);
    try {
      // Delete existing hours
      await supabase
        .from("staff_hours")
        .delete()
        .eq("staff_id", managingHoursStaffId);

      // Insert new hours (only for days that are not off)
      const hoursToInsert = staffHours
        .filter(h => !h.is_off)
        .map(h => ({
          staff_id: managingHoursStaffId,
          day_of_week: h.day_of_week,
          start_time: h.start_time,
          end_time: h.end_time,
          is_off: false,
        }));

      if (hoursToInsert.length > 0) {
        const { error } = await supabase
          .from("staff_hours")
          .insert(hoursToInsert);

        if (error) throw error;
      }

      toast({
        title: "Hours saved",
        description: "Staff working hours have been updated successfully.",
      });

      setIsHoursDialogOpen(false);
      setManagingHoursStaffId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save staff hours",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Staff Services Assignment
  const handleAssignServices = async (member: typeof staff[0]) => {
    setAssigningServicesStaffId(member.id);

    // Fetch assigned services
    const { data: assignedServices, error } = await supabase
      .from("staff_services")
      .select("service_id")
      .eq("staff_id", member.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load assigned services",
        variant: "destructive",
      });
      return;
    }

    setAssignedServiceIds(assignedServices?.map(s => s.service_id) || []);
    setIsServicesDialogOpen(true);
  };

  const handleSaveStaffServices = async () => {
    if (!assigningServicesStaffId) return;

    setIsSaving(true);
    try {
      // Delete existing assignments
      await supabase
        .from("staff_services")
        .delete()
        .eq("staff_id", assigningServicesStaffId);

      // Insert new assignments
      if (assignedServiceIds.length > 0) {
        const assignments = assignedServiceIds.map(serviceId => ({
          staff_id: assigningServicesStaffId,
          service_id: serviceId,
        }));

        const { error } = await supabase
          .from("staff_services")
          .insert(assignments);

        if (error) throw error;
      }

      toast({
        title: "Services assigned",
        description: "Staff service assignments have been updated successfully.",
      });

      setIsServicesDialogOpen(false);
      setAssigningServicesStaffId(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to assign services",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleServiceAssignment = (serviceId: string) => {
    setAssignedServiceIds(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleToggleDay = (index: number) => {
    const newSchedule = [...schedule];
    newSchedule[index].isOpen = !newSchedule[index].isOpen;
    setSchedule(newSchedule);
  };

  const handleTimeChange = (index: number, field: "openTime" | "closeTime", value: string) => {
    const newSchedule = [...schedule];
    newSchedule[index][field] = value;
    setSchedule(newSchedule);
  };

  const applyMondayToWeekdays = () => {
    const mondaySchedule = schedule[1];
    const newSchedule = schedule.map((day, index) => {
      if (index >= 1 && index <= 5) {
        return { ...mondaySchedule };
      }
      return day;
    });
    setSchedule(newSchedule);
  };

  const openAllDays = () => {
    setSchedule(schedule.map(day => ({ ...day, isOpen: true })));
  };

  // Load break times for business
  const loadBreakTimes = async () => {
    if (!business) return;
    try {
      const { data, error } = await supabase
        .from("break_times" as any)
        .select("*")
        .eq("business_id", business.id)
        .is("location_id", null)
        .order("day_of_week")
        .order("start_time");

      if (error) throw error;

      const breaksByDay: Record<number, BreakTime[]> = {};
      (data || []).forEach((breakTime: any) => {
        if (!breaksByDay[breakTime.day_of_week]) {
          breaksByDay[breakTime.day_of_week] = [];
        }
        breaksByDay[breakTime.day_of_week].push({
          id: breakTime.id,
          startTime: breakTime.start_time,
          endTime: breakTime.end_time,
          name: breakTime.name || undefined,
        });
      });
      setBreakTimes(breaksByDay);
    } catch (error) {
      console.error("Error loading break times:", error);
    }
  };

  // Load break times when business loads
  useEffect(() => {
    if (business) {
      loadBreakTimes();
    }
  }, [business]);

  const handleSaveSchedule = async () => {
    if (!business) {
      toast({
        title: "Error",
        description: "No business found",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // Delete existing business hours for this business (default location)
      await supabase
        .from("business_hours")
        .delete()
        .eq("business_id", business.id)
        .is("location_id", null);

      // Insert new business hours
      const hoursToInsert = schedule.map((day, index) => ({
        business_id: business.id,
        day_of_week: index,
        open_time: day.isOpen ? day.openTime : "09:00",
        close_time: day.isOpen ? day.closeTime : "17:00",
        is_closed: !day.isOpen,
        location_id: null, // Default location
      }));

      const { error: hoursError } = await supabase
        .from("business_hours")
        .insert(hoursToInsert);

      if (hoursError) throw hoursError;

      // Delete existing break times for this business (default location)
      await supabase
        .from("break_times" as any)
        .delete()
        .eq("business_id", business.id)
        .is("location_id", null);

      // Insert new break times
      const breaksToInsert: any[] = [];
      Object.entries(breakTimes).forEach(([dayOfWeekStr, breaks]) => {
        const dayOfWeek = parseInt(dayOfWeekStr, 10);
        breaks.forEach((breakTime) => {
          breaksToInsert.push({
            business_id: business.id,
            location_id: null, // Default location
            day_of_week: dayOfWeek,
            start_time: breakTime.startTime,
            end_time: breakTime.endTime,
            name: breakTime.name || null,
          });
        });
      });

      if (breaksToInsert.length > 0) {
        const { error: breaksError } = await supabase
          .from("break_times" as any)
          .insert(breaksToInsert);

        if (breaksError) throw breaksError;
      }

      toast({
        title: "Schedule saved",
        description: "Your working hours and break times have been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving schedule:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save schedule",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const emptyServiceHours = () => DAYS.map((_, day_of_week) => ({
    day_of_week,
    start_time: schedule[day_of_week]?.openTime || "09:00",
    end_time: schedule[day_of_week]?.closeTime || "17:00",
    is_off: true,
  }));

  const loadServiceHours = async (serviceId: string) => {
    if (!serviceId) {
      setServiceHours(emptyServiceHours());
      return;
    }
    setServiceHoursLoading(true);
    try {
      const { data, error } = await supabase
        .from("service_hours")
        .select("day_of_week,start_time,end_time")
        .eq("service_id", serviceId)
        .order("day_of_week");
      if (error) throw error;
      const rows = data || [];
      setServiceHours(DAYS.map((_, day_of_week) => {
        const row = rows.find((hour) => hour.day_of_week === day_of_week);
        return {
          day_of_week,
          start_time: row?.start_time?.slice(0, 5) || schedule[day_of_week]?.openTime || "09:00",
          end_time: row?.end_time?.slice(0, 5) || schedule[day_of_week]?.closeTime || "17:00",
          is_off: !row,
        };
      }));
    } catch (error) {
      console.error("Error loading service hours:", error);
      toast({
        title: "Could not load service hours",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setServiceHoursLoading(false);
    }
  };

  const openServiceHoursDialog = () => {
    const firstServiceId = (services || []).find((service: any) => service?.active !== false)?.id || "";
    setServiceHoursServiceId(firstServiceId);
    setServiceHoursDialogOpen(true);
    void loadServiceHours(firstServiceId);
  };

  const persistServiceHours = async (enabled: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
  }>) => {
    if (!business || !serviceHoursServiceId) return;
    setIsSaving(true);
    try {
      const { error: deleteError } = await supabase
        .from("service_hours")
        .delete()
        .eq("service_id", serviceHoursServiceId);
      if (deleteError) throw deleteError;
      if (enabled.length > 0) {
        const { error: insertError } = await supabase.from("service_hours").insert(
          enabled.map((hour) => ({
            business_id: business.id,
            service_id: serviceHoursServiceId,
            day_of_week: hour.day_of_week,
            start_time: hour.start_time,
            end_time: hour.end_time,
          })),
        );
        if (insertError) throw insertError;
      }
      toast({
        title: enabled.length ? "Service hours saved" : "Regular hours restored",
        description: enabled.length
          ? "The service can now be booked only during these weekly hours."
          : "This service now follows your regular business hours.",
      });
      setServiceHoursDialogOpen(false);
    } catch (error) {
      console.error("Error saving service hours:", error);
      toast({
        title: "Could not save service hours",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const saveServiceHours = async () => {
    if (!business || !serviceHoursServiceId) return;
    const enabled = serviceHours.filter((hour) => !hour.is_off);
    if (enabled.some((hour) => !hour.start_time || !hour.end_time || hour.start_time >= hour.end_time)) {
      toast({
        title: "Check service hours",
        description: "Each selected day must end after it starts.",
        variant: "destructive",
      });
      return;
    }
    await persistServiceHours(enabled.map((hour) => ({
      day_of_week: hour.day_of_week,
      start_time: hour.start_time,
      end_time: hour.end_time,
    })));
  };

  const clearServiceHours = async () => {
    if (!business || !serviceHoursServiceId) return;
    setServiceHours(emptyServiceHours());
    await persistServiceHours([]);
  };

  // Break time management functions
  const openBreakTimeDialog = (dayIndex: number, breakTime?: BreakTime) => {
    setBreakTimeDialogDay(dayIndex);
    if (breakTime) {
      setEditingBreakTime(breakTime);
      setBreakTimeForm(breakTime);
    } else {
      setEditingBreakTime(null);
      setBreakTimeForm({ startTime: "12:00", endTime: "13:00", name: "" });
    }
    setBreakTimeDialogOpen(true);
  };

  const saveBreakTime = () => {
    if (breakTimeDialogDay === null) return;
    if (breakTimeForm.startTime >= breakTimeForm.endTime) {
      toast({
        title: "Invalid time",
        description: "Start time must be before end time",
        variant: "destructive",
      });
      return;
    }

    const dayBreaks = breakTimes[breakTimeDialogDay] || [];
    if (editingBreakTime && editingBreakTime.id) {
      // Update existing break
      const updated = dayBreaks.map((bt) =>
        bt.id === editingBreakTime.id ? { ...breakTimeForm, id: editingBreakTime.id } : bt
      );
      setBreakTimes({ ...breakTimes, [breakTimeDialogDay]: updated });
    } else {
      // Add new break
      setBreakTimes({
        ...breakTimes,
        [breakTimeDialogDay]: [...dayBreaks, breakTimeForm],
      });
    }
    setBreakTimeDialogOpen(false);
    setBreakTimeForm({ startTime: "12:00", endTime: "13:00", name: "" });
  };

  const deleteBreakTime = (dayIndex: number, breakTime: BreakTime) => {
    const dayBreaks = breakTimes[dayIndex] || [];
    if (breakTime.id) {
      // Remove from DB will happen on save
      setBreakTimes({
        ...breakTimes,
        [dayIndex]: dayBreaks.filter((bt) => bt.id !== breakTime.id),
      });
    } else {
      // Remove from local state
      setBreakTimes({
        ...breakTimes,
        [dayIndex]: dayBreaks.filter((bt) => bt !== breakTime),
      });
    }
  };

  const openSupportEmail = async () => {
    const name = supportBusinessName.trim() || businessName || business?.name || "My business";
    const message = supportMessage.trim();

    if (!message) {
      toast({
        title: "Describe the problem",
        description: "Add a short message so support knows what to help with.",
        variant: "destructive",
      });
      return;
    }

    setSupportSending(true);
    try {
      const { error } = await supabase.functions.invoke("send-support-email", {
        body: {
          businessId: business?.id,
          businessName: name,
          message,
        },
      });

      if (error) throw error;

      setSupportMessage("");
      toast({
        title: "Support request sent",
        description: "Thanks. Our team will reply as soon as possible.",
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Please try again or contact us on WhatsApp.";
      toast({
        title: "Couldn’t send support request",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSupportSending(false);
    }
  };

  const openAppRating = () => {
    const platform = Capacitor.getPlatform();
    const url =
      platform === "android"
        ? PLAY_STORE_REVIEW_URL
        : platform === "ios"
          ? IOS_APP_STORE_REVIEW_URL
          : PLAY_STORE_REVIEW_URL;

    window.open(url, "_blank", "noopener,noreferrer");

    if (platform === "ios" && !IOS_APP_STORE_ID) {
      toast({
        title: "Opening App Store search",
        description: "Add VITE_IOS_APP_STORE_ID after App Store Connect assigns the app ID to open the review screen directly.",
      });
    }
  };

  if (businessLoading) {
  return (
      <AppShell businessName="Loading...">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  const primaryModule = ((business as any)?.primary_module || "appointments") as string;
  const primaryModuleLabel =
    primaryModule === "memberships"
      ? "Memberships"
      : primaryModule === "catering_services" || primaryModule === "food_vendors"
        ? "Catering Services"
      : primaryModule === "events"
        ? "Events"
        : primaryModule === "walk_ins"
          ? "Walk-Ins"
          : primaryModule === "clinics"
            ? "Clinics"
            : "Appointments";
  const isCateringModule = primaryModule === "catering_services" || primaryModule === "food_vendors";
  const availableDirectMethods = REGION_METHODS[paymentRegion];
  const availableBanks = REGION_BANKS[paymentRegion] || [];
  const availableMobileMoneyOptions = MOBILE_MONEY_OPTIONS[paymentRegion] || [];
  const availablePayoutMethods = (
    availableDirectMethods.filter((method) => method === "bank" || method === "momo" || method === "mpesa") as DirectPaymentMethod[]
  ).length
    ? (availableDirectMethods.filter((method) => method === "bank" || method === "momo" || method === "mpesa") as DirectPaymentMethod[])
    : (["bank"] as DirectPaymentMethod[]);
  const directPaymentMethod = availableDirectMethods.includes(paymentMethod as DirectPaymentMethod)
    ? (paymentMethod as DirectPaymentMethod)
    : availableDirectMethods[0];
  const payoutPaymentMethod = availablePayoutMethods.includes(paymentMethod as DirectPaymentMethod)
    ? (paymentMethod as DirectPaymentMethod)
    : availablePayoutMethods[0];
  const payoutDestinationReady =
    (payoutPaymentMethod === "bank" && !!paymentBank.trim() && !!paymentAccountNumber.trim())
    || (
      (payoutPaymentMethod === "momo" || payoutPaymentMethod === "mpesa")
      && !!paymentMobileMoneyNumber.trim()
      && (availableMobileMoneyOptions.length === 0 || !!paymentMobileMoneyNetwork.trim())
    );
  const defaultLocationForPayments =
    locations.find((location) => (location as Tables<"locations"> & { is_default?: boolean | null }).is_default) ||
    locations[0];
  const detectedPaymentRegion = inferPaymentRegion(
    String((defaultLocationForPayments as (Tables<"locations"> & { country?: string | null }) | undefined)?.country || ""),
    currency,
  );
  const generatedPaymentInstructions = buildDirectPaymentInstructions({
    method: directPaymentMethod,
    region: paymentRegion,
    businessName,
    bank: paymentBank,
    accountNumber: paymentAccountNumber,
    mobileNetwork: paymentMobileMoneyNetwork,
    mobileNumber: paymentMobileMoneyNumber,
    fallbackInstructions: bookingDepositPaymentInstructions,
  });
  const hasConfiguredPaystackAccount =
    !!(business as any)?.paystack_connected_account_enabled ||
    (businessPaystackEnabled && (!!businessPaystackMasked || !!businessPaystackSecretKey.trim()));
  const usesOwnPaystackCheckout = businessPaystackEnabled;
  const usesOnlineCheckout = collectBookingDeposits || usesOwnPaystackCheckout;
  const showPayAtVenue = usesOwnPaystackCheckout && hasConfiguredPaystackAccount && !!(business as any)?.slug;
  const settingsSections: Array<{
    id: SettingsSectionId;
    label: string;
    description: string;
    icon: typeof Building;
    badge?: string;
  }> = [
    ...(isNativeIos
      ? [{
          id: "usage" as SettingsSectionId,
          label: "Organization Usage",
          description: "View current usage across bookings, staff, locations, customers, and campaigns.",
          icon: BarChart3,
        }]
      : []),
    {
      id: "business",
      label: "Business Details",
      description: "Brand, contact info, links, business preferences, and booking link.",
      icon: Building,
    },
    {
      id: "schedule",
      label: "Schedule Management",
      description: "Manage opening hours, breaks, and business availability.",
      icon: Clock,
    },
    {
      id: "staff",
      label: "Staff Management",
      description: "Add team members, assign roles, and manage services.",
      icon: Users,
    },
    {
      id: "booking",
      label: "Online Booking",
      description: "Customize your booking page, branding, and booking experience.",
      icon: Palette,
    },
    {
      id: "payment",
      label: "Payments & Payouts",
      description: "Checkout, payout account (MoMo or bank), and payment settings.",
      icon: CreditCard,
    },
    {
      id: "coupons",
      label: "Coupons",
      description: "Create discounts, promo codes, and usage rules.",
      icon: Tag,
    },
    {
      id: "locations",
      label: "Locations",
      description:
        "Branches and addresses used for bookings, staff, and services.",
      icon: MapPin,
    },
    {
      id: "notifications",
      label: "Notifications",
      description: "Control alerts, reminder channels, and notification behavior.",
      icon: Bell,
    },
    {
      id: "support",
      label: "Support",
      description: "Contact Buukd support by email or WhatsApp customer care.",
      icon: MessageCircle,
    },
  ];
  const filteredSettingsSections = settingsSections.filter((section) => {
    const q = settingsSearch.trim().toLowerCase();
    if (!q) return true;
    return `${section.label} ${section.description}`.toLowerCase().includes(q);
  });
  const activeSettingsSectionMeta =
    settingsSections.find((section) => section.id === activeSettingsSection) || null;

  const pwaUpdatesCard = pwaUpdate.isSupported ? (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
          <div className="mt-0.5 shrink-0 rounded-full bg-muted p-2.5">
            <RefreshCw className="h-5 w-5 text-foreground" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-sm text-foreground sm:text-base">Check for updates</h3>
            <p className="mt-1 text-sm text-muted-foreground">Get the latest fixes and features.</p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full shrink-0 gap-2 sm:w-auto"
          onClick={() => {
            pwaUpdate.checkForUpdates();
            toast({
              title: "Checking for updates…",
              description: "If a new version is found, the app will reload.",
            });
          }}
          disabled={pwaUpdate.checking}
        >
          {pwaUpdate.checking ? (
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
          ) : (
            <RefreshCw className="h-4 w-4 shrink-0" />
          )}
          <span className="hidden sm:inline">Check for updates</span>
          <span className="sm:hidden">Check now</span>
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader title="Settings" description="Manage your business settings" />

      {!activeSettingsSection ? (
        <div className="space-y-4 sm:space-y-6">
          <div className="rounded-xl border border-border bg-card p-3 sm:p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={settingsSearch}
                onChange={(e) => setSettingsSearch(e.target.value)}
                placeholder="Search settings"
                className="pl-9 input-sleek"
              />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {filteredSettingsSections.length > 0 ? (
              filteredSettingsSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setActiveSettingsSection(section.id)}
                    className={`w-full px-4 sm:px-5 py-4 text-left transition-colors hover:bg-muted/40 ${
                      index !== filteredSettingsSections.length - 1 ? "border-b border-border" : ""
                    }`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="mt-0.5 shrink-0 rounded-full bg-muted p-2.5">
                        <Icon className="h-5 w-5 text-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-sm sm:text-base text-foreground">{section.label}</h3>
                          {section.badge ? <Badge variant="secondary">{section.badge}</Badge> : null}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
                      </div>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                No settings matched your search.
              </div>
            )}
          </div>
          {pwaUpdatesCard}
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          <div className="space-y-3 sm:space-y-4">
            <Button
              type="button"
              variant="ghost"
              className="-ml-2 w-fit gap-2 text-muted-foreground hover:text-foreground"
              onClick={() => setActiveSettingsSection(null)}
            >
              <ChevronLeft className="h-4 w-4 shrink-0" />
              All settings
            </Button>
            {activeSettingsSectionMeta ? (
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-foreground">{activeSettingsSectionMeta.label}</h2>
                <p className="text-sm text-muted-foreground">{activeSettingsSectionMeta.description}</p>
              </div>
            ) : null}
          </div>

          <Tabs value={activeSettingsSection} className="w-full">

        {/* Organization Usage Tab */}
        {isNativeIos && (
        <TabsContent value="usage" className="space-y-4 sm:space-y-6">
          <OrganizationUsageSettings />
        </TabsContent>
        )}

        {/* Business Details Tab */}
        <TabsContent value="business" className="space-y-4 sm:space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="space-y-1">
                <h3 className="font-semibold text-base sm:text-lg">Business Information</h3>
                <p className="text-sm text-muted-foreground">Keep the essentials clean, compact, and easy to update.</p>
              </div>

              <div className="rounded-lg border border-border bg-accent/20 px-3 py-2 flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground">Primary module</span>
                <Badge variant="secondary" className="capitalize">{primaryModuleLabel}</Badge>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 space-y-5">
                  <div className="space-y-1 pb-1">
                    <h4 className="font-semibold text-base text-foreground">Basic Details</h4>
                    <p className="text-sm text-muted-foreground">
                      Update the main information customers and staff see most often.
                    </p>
                  </div>

                  <div className="rounded-xl border border-border bg-muted/20 p-3 sm:p-4 space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2.5">
                        <Label htmlFor="businessName" className="text-sm font-medium">Business Name</Label>
                        <Input
                          id="businessName"
                          value={businessName}
                          readOnly
                          disabled
                          className="input-sleek h-12 bg-muted/60 cursor-not-allowed text-muted-foreground"
                        />
                      </div>
                      <div className="space-y-2.5">
                        <Label htmlFor="email" className="text-sm font-medium">Business Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={businessEmail}
                          readOnly
                          disabled
                          className="input-sleek h-12 bg-muted/60 cursor-not-allowed text-muted-foreground"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2.5">
                      <Label htmlFor="displayName" className="text-sm font-medium">Your Display Name</Label>
                      <Input
                        id="displayName"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Enter your name"
                        className="input-sleek h-12"
                      />
                    </div>
                    <div className="space-y-2.5">
                      <Label className="text-sm font-medium">App Accent Color</Label>
                      <Select value={accentTheme} onValueChange={(v: AccentTheme) => setAccentTheme(v)}>
                        <SelectTrigger className="input-sleek h-12 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="blue">Blue</SelectItem>
                          <SelectItem value="pink">Pink</SelectItem>
                          <SelectItem value="purple">Purple</SelectItem>
                          <SelectItem value="lavender">Lavender</SelectItem>
                          <SelectItem value="teal">Teal</SelectItem>
                          <SelectItem value="navy">Navy</SelectItem>
                          <SelectItem value="charcoal">Charcoal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    <Label htmlFor="description" className="text-sm font-medium">Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Tell customers what your business does"
                      className="input-sleek min-h-[120px] rounded-2xl"
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-medium text-sm sm:text-base">Contact & Links</h4>
                    <Button type="button" variant="outline" size="sm" onClick={openAddPrimarySocialDialog}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add social
                    </Button>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm">Business Phone</Label>
                      <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-2">
                        <Select value={phoneCountryCode} onValueChange={setPhoneCountryCode}>
                          <SelectTrigger className="input-sleek w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="+233">GH +233</SelectItem>
                            <SelectItem value="+1">US +1</SelectItem>
                            <SelectItem value="+44">UK +44</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          id="phone"
                          placeholder="Phone number"
                          className="input-sleek"
                          value={businessPhoneLocal}
                          onChange={(e) => setBusinessPhoneLocal(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="whatsapp" className="text-sm">WhatsApp</Label>
                      <div className="grid gap-2">
                        <Input
                          id="whatsapp"
                          value={whatsappUrl}
                          onChange={(e) => setWhatsappUrl(e.target.value)}
                          placeholder="https://wa.me/233XXXXXXXXX"
                          className="input-sleek"
                        />
                        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                          <Input
                            placeholder="Label (optional)"
                            className="input-sleek h-10"
                            value={primarySocialLabels.whatsapp}
                            onChange={(e) => setPrimarySocialLabels((prev) => ({ ...prev, whatsapp: e.target.value }))}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={() => {
                              const digits = (businessPhoneLocal || "").replace(/[^\d]/g, "");
                              if (!digits) {
                                toast({
                                  title: "Add a phone number first",
                                  description: "Enter your business phone number to generate a WhatsApp link.",
                                  variant: "destructive",
                                });
                                return;
                              }
                              const selectedCC = String(phoneCountryCode || "+233").replace("+", "");
                              const e164 = phoneToE164(`${phoneCountryCode}${digits}`, selectedCC) || "";
                              setWhatsappUrl(`https://wa.me/${e164.replace("+", "")}`);
                              toast({ title: "WhatsApp link generated" });
                            }}
                          >
                            Generate from phone
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {primarySocialPlatforms.some((option) => String(getPrimarySocialUrl(option.platform) || "").trim()) ? (
                    <div className="space-y-2">
                      {primarySocialPlatforms
                        .filter((option) => String(getPrimarySocialUrl(option.platform) || "").trim())
                        .map((option) => (
                          <div key={option.platform} className="rounded-lg border border-border bg-background p-3">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <Badge variant="secondary" className="capitalize">{option.label}</Badge>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => handleRemovePrimarySocial(option.platform)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_200px]">
                              <Input
                                value={getPrimarySocialUrl(option.platform)}
                                onChange={(e) => setPrimarySocialUrlByPlatform(option.platform, e.target.value)}
                                placeholder={option.placeholder}
                                className="input-sleek"
                              />
                              <Input
                                value={primarySocialLabels[option.platform]}
                                onChange={(e) => setPrimarySocialLabelByPlatform(option.platform, e.target.value)}
                                placeholder="Label (optional)"
                                className="input-sleek"
                              />
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : null}

                  <div className="rounded-lg border border-border bg-muted/20 p-3">
                    {!showExtraLinksSection ? (
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm text-foreground">Extra social accounts</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowExtraLinksSection(true)}>
                          Manage
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-foreground">Extra social accounts</span>
                          <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={() => setSocialDialogOpen(true)}>
                              <Plus className="h-4 w-4 mr-2" />
                              Add
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => setShowExtraLinksSection(false)}>
                              Done
                            </Button>
                          </div>
                        </div>
                        {extraSocialLinks.filter((l: any) => !l?.is_primary).length > 0 ? (
                          <div className="space-y-2">
                            {extraSocialLinks
                              .filter((l: any) => !l?.is_primary)
                              .map((l: any) => (
                                <div key={l.id} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background p-3">
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary" className="text-xs capitalize">{String(l.platform || "")}</Badge>
                                      <span className="text-sm text-foreground truncate">{String(l.label || "Extra account")}</span>
                                    </div>
                                    <a
                                      href={String(l.url)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-xs text-primary hover:underline break-all"
                                    >
                                      {String(l.url)}
                                    </a>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive"
                                    onClick={() => handleRemoveExtraSocial(String(l.id))}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Remove
                                  </Button>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No extra accounts yet.</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
                  <h4 className="font-medium text-sm sm:text-base">Preferences</h4>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="timezone" className="text-sm">Timezone</Label>
                      <Select value={timezone} onValueChange={setTimezone}>
                        <SelectTrigger className="input-sleek">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Africa/Accra">Accra (GMT+0)</SelectItem>
                          <SelectItem value="Africa/Lagos">Lagos (GMT+1)</SelectItem>
                          <SelectItem value="Africa/Johannesburg">Johannesburg (GMT+2)</SelectItem>
                          <SelectItem value="Europe/London">London (GMT+0)</SelectItem>
                          <SelectItem value="America/New_York">New York (GMT-5)</SelectItem>
                          <SelectItem value="America/Los_Angeles">Los Angeles (GMT-8)</SelectItem>
                          <SelectItem value="UTC">UTC</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currency" className="text-sm">Currency</Label>
                      <Select value={currency} onValueChange={setCurrency}>
                        <SelectTrigger className="input-sleek">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="GHS">GHS - ₵</SelectItem>
                          <SelectItem value="NGN">NGN - ₦</SelectItem>
                          <SelectItem value="USD">USD - $</SelectItem>
                          <SelectItem value="EUR">EUR - €</SelectItem>
                          <SelectItem value="GBP">GBP - £</SelectItem>
                          <SelectItem value="ZAR">ZAR - R</SelectItem>
                          <SelectItem value="KES">KES - KSh</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {primaryModule === "memberships" ? (
                      <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">Access Desk</p>
                        </div>
                        <Switch checked={accessDeskEnabled} onCheckedChange={setAccessDeskEnabled} />
                      </div>
                    ) : null}

                    {isCateringModule ? (
                      <>
                        <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">Orders</p>
                          </div>
                          <Switch checked disabled />
                        </div>
                        <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">Event / catering requests</p>
                          </div>
                          <Switch checked={eventCateringRequestsEnabled} onCheckedChange={setEventCateringRequestsEnabled} />
                        </div>
                        <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">Multiple menus</p>
                          </div>
                          <Switch checked={ordersMultipleMenusEnabled} onCheckedChange={setOrdersMultipleMenusEnabled} />
                        </div>
                      </>
                    ) : null}

                    {primaryModule === "appointments" ? (
                      <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-foreground">Invoicing</p>
                        </div>
                        <Switch checked={appointmentsInvoicingEnabled} onCheckedChange={setAppointmentsInvoicingEnabled} />
                      </div>
                    ) : null}

                    {(primaryModule === "appointments" || primaryModule === "clinics") ? (
                      <>
                        <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">Log Book</p>
                          </div>
                          <Switch checked={logBookEnabled} onCheckedChange={setLogBookEnabled} />
                        </div>
                        <div className="rounded-lg border border-border bg-background px-3 py-3 flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">Sales</p>
                          </div>
                          <Switch checked={salesEnabled} onCheckedChange={setSalesEnabled} />
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
                  <h4 className="font-medium text-sm sm:text-base">Location & Booking</h4>
                  <div className="space-y-2">
                    <Label htmlFor="bookingUrl" className="text-sm">Booking URL</Label>
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <Input
                        id="bookingUrl"
                        value={business?.slug ? `${getBaseUrl()}/${business.slug}` : ""}
                        readOnly
                        className="input-sleek bg-muted/50"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={async () => {
                          const url = business?.slug ? `${getBaseUrl()}/${business.slug}` : "";
                          if (!url) return;
                          await navigator.clipboard.writeText(url);
                          toast({ title: "Booking link copied" });
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location" className="text-sm">Location</Label>
                    <MapPicker
                      address={locationAddress}
                      onAddressChange={(v) => {
                        setLocationAddress(v);
                        setLocationPlaceId("");
                        setLocationLat(null);
                        setLocationLng(null);
                        setLocationCity("");
                        setLocationCountry("");
                      }}
                      lat={locationLat}
                      lng={locationLng}
                      onLatLngChange={(pos) => {
                        setLocationLat(pos?.lat ?? null);
                        setLocationLng(pos?.lng ?? null);
                      }}
                      onPlaceSelected={(p) => {
                        setLocationAddress(p.formattedAddress);
                        setLocationPlaceId(p.placeId || "");
                        setLocationCity(p.city || "");
                        setLocationCountry(p.country || "");
                        setLocationLat(typeof p.lat === "number" ? p.lat : null);
                        setLocationLng(typeof p.lng === "number" ? p.lng : null);
                      }}
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>

              <Dialog open={primarySocialDialogOpen} onOpenChange={setPrimarySocialDialogOpen}>
                <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)]">
                  <DialogHeader>
                    <DialogTitle>Add social link</DialogTitle>
                    <DialogDescription>Add another public social profile without crowding the main form.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="space-y-2">
                      <Label className="text-sm">Platform</Label>
                      <Select value={primarySocialPlatform} onValueChange={(v) => setPrimarySocialPlatform(v as AddableSocialPlatform)}>
                        <SelectTrigger className="input-sleek">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {primarySocialPlatforms.map((option) => (
                            <SelectItem key={option.platform} value={option.platform}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">URL</Label>
                      <Input
                        className="input-sleek"
                        value={primarySocialUrlDraft}
                        onChange={(e) => setPrimarySocialUrlDraft(e.target.value)}
                        placeholder={SOCIAL_PLATFORM_OPTIONS.find((option) => option.platform === primarySocialPlatform)?.placeholder || "https://..."}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Label (optional)</Label>
                      <Input
                        className="input-sleek"
                        value={primarySocialLabelDraft}
                        onChange={(e) => setPrimarySocialLabelDraft(e.target.value)}
                        placeholder="e.g. Main, Shop, Careers"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setPrimarySocialDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleAddPrimarySocial}>
                      Add link
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={socialDialogOpen} onOpenChange={setSocialDialogOpen}>
                <DialogContent className="sm:max-w-lg w-[calc(100%-2rem)]">
                  <DialogHeader>
                    <DialogTitle>Add social account</DialogTitle>
                    <DialogDescription>Customers will choose between accounts when they tap the icon.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="space-y-2">
                      <Label className="text-sm">Platform</Label>
                      <Select value={socialPlatform} onValueChange={(v) => setSocialPlatform(v as any)}>
                        <SelectTrigger className="input-sleek">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="instagram">Instagram</SelectItem>
                          <SelectItem value="facebook">Facebook</SelectItem>
                          <SelectItem value="twitter">Twitter</SelectItem>
                          <SelectItem value="linkedin">LinkedIn</SelectItem>
                          <SelectItem value="tiktok">TikTok</SelectItem>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Label (optional)</Label>
                      <Input
                        className="input-sleek"
                        value={socialLabel}
                        onChange={(e) => setSocialLabel(e.target.value)}
                        placeholder="e.g., Main, Catering, Shop"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">URL</Label>
                      <Input
                        className="input-sleek"
                        value={socialUrlNew}
                        onChange={(e) => setSocialUrlNew(e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setSocialDialogOpen(false)} disabled={isSavingSocial}>
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleAddExtraSocial} disabled={isSavingSocial}>
                      {isSavingSocial ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Add account"
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => void resetBusinessTabState()}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveSettings} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4 sm:space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <h3 className="font-semibold text-base sm:text-lg">Working Hours</h3>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 sm:p-4 flex items-start gap-2 sm:gap-3">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs sm:text-sm text-primary">
                  Set your regular working hours for each day. These hours will be used to determine when clients can book appointments.
                </p>
              </div>

              {/* Quick actions - stacked on mobile */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <Button variant="outline" onClick={applyMondayToWeekdays} className="flex items-center justify-center gap-2 text-xs sm:text-sm">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">Apply Monday hours to all weekdays</span>
                  <span className="sm:hidden">Apply to weekdays</span>
                </Button>
                <Button variant="outline" onClick={openAllDays} className="flex items-center justify-center gap-2 text-xs sm:text-sm">
                  <Calendar className="h-4 w-4" />
                  Open all days
                </Button>
              </div>

              {/* Schedule grid - single column on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {DAYS.map((day, index) => (
                  <Card key={day} className="border border-border">
                    <CardContent className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm sm:text-base">
                            <span className="sm:hidden">{DAYS_SHORT[index]}</span>
                            <span className="hidden sm:inline">{day}</span>
                          </span>
                          {schedule[index].isOpen && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Open</span>
                          )}
                        </div>
                        <Switch
                          checked={schedule[index].isOpen}
                          onCheckedChange={() => handleToggleDay(index)}
                        />
                      </div>
                      
                      {schedule[index].isOpen && (
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Opens</Label>
                            <div className="flex items-center gap-1.5 sm:gap-2 border border-border rounded-lg px-2 sm:px-3 py-2">
                              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                              <Input
                                type="time"
                                value={schedule[index].openTime}
                                onChange={(e) => handleTimeChange(index, "openTime", e.target.value)}
                                className="border-0 p-0 h-auto text-xs sm:text-sm focus-visible:ring-0"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Closes</Label>
                            <div className="flex items-center gap-1.5 sm:gap-2 border border-border rounded-lg px-2 sm:px-3 py-2">
                              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                              <Input
                                type="time"
                                value={schedule[index].closeTime}
                                onChange={(e) => handleTimeChange(index, "closeTime", e.target.value)}
                                className="border-0 p-0 h-auto text-xs sm:text-sm focus-visible:ring-0"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {schedule[index].isOpen && (
                        <div className="space-y-2">
                          <button 
                            onClick={() => openBreakTimeDialog(index)}
                            className="text-xs sm:text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <Plus className="h-3 w-3" />
                            Add Break Time
                        </button>
                          {breakTimes[index] && breakTimes[index].length > 0 && (
                            <div className="space-y-1.5">
                              {breakTimes[index].map((breakTime, breakIndex) => (
                                <div 
                                  key={breakIndex} 
                                  className="flex items-center justify-between p-2 bg-muted/50 rounded-lg text-xs"
                                >
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="text-muted-foreground">
                                      {breakTime.startTime} - {breakTime.endTime}
                                    </span>
                                    {breakTime.name && (
                                      <span className="text-foreground font-medium truncate">
                                        {breakTime.name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => openBreakTimeDialog(index, breakTime)}
                                      className="p-1 hover:bg-background rounded"
                                    >
                                      <MoreHorizontal className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={() => deleteBreakTime(index, breakTime)}
                                      className="p-1 hover:bg-destructive/10 text-destructive rounded"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Reset to saved schedule
                    const fetchBusinessHours = async () => {
                      if (!business) return;
                      try {
                        const { data: hoursData, error } = await supabase
                          .from("business_hours")
                          .select("*")
                          .eq("business_id", business.id)
                          .is("location_id", null);

                        if (error) throw error;

                        if (hoursData && hoursData.length > 0) {
                          const newSchedule = DAYS.map((_, index) => {
                            const dayHours = hoursData.find((h) => h.day_of_week === index);
                            if (dayHours) {
                              return {
                                isOpen: !dayHours.is_closed,
                                openTime: dayHours.open_time || "09:00",
                                closeTime: dayHours.close_time || "17:00",
                              };
                            }
                            return defaultSchedule[index];
                          });
                          setSchedule(newSchedule);
                        } else {
                          setSchedule(defaultSchedule);
                        }
                      } catch (err) {
                        console.error("Error loading business hours:", err);
                      }
                    };
                    fetchBusinessHours();
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveSchedule} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-base sm:text-lg">Service-specific hours</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Offer selected services only on particular weekdays and times. For example, Tuesdays and Thursdays every week.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={openServiceHoursDialog}
                  disabled={(services || []).filter((service: any) => service?.active !== false).length === 0}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Set service hours
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff" className="space-y-4 sm:space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h3 className="font-semibold text-base sm:text-lg">
                  {isCateringModule ? "Delivery team" : "Team members"}
                </h3>
                <Button
                  size="sm"
                  className="w-full sm:w-auto"
                  onClick={() => {
                    resetStaffForm();
                    setIsStaffDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {isCateringModule ? "Add rider" : "Add staff"}
                </Button>
              </div>
              
              {staffLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : staff.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No team members yet.</p>
                  <p className="text-sm text-muted-foreground mt-2">Use Add Staff to invite your first team member.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {staff.map((member) => (
                    <div
                      key={member.id}
                      className="p-3 sm:p-4 border border-border rounded-xl hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <Avatar className="h-10 w-10 flex-shrink-0">
                            <AvatarFallback className="bg-primary/10 text-primary text-sm">
                              {member.name.split(" ").map((n) => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">{member.name}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">{member.email}</p>
                          </div>
                        </div>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9 flex-shrink-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditStaff(member)}>
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleManageHours(member)}>
                              Manage Hours
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAssignServices(member)}>
                              Assign Services
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => handleDeleteStaff(member.id, member.name)}
                            >
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className="text-xs capitalize">
                          {member.role || "staff"}
                        </Badge>
                        {!member.active && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customize Booking Page Tab */}
        <TabsContent value="booking" className="space-y-4 sm:space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <h3 className="font-semibold text-base sm:text-lg">Customize Booking Page</h3>
              
              <div className="space-y-2">
                <Label className="text-sm">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={primaryColor}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === "") {
                        setPrimaryColor(value);
                      }
                    }}
                    placeholder="#2563EB"
                    className="input-sleek flex-1"
                    maxLength={7}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  This color will be used for buttons and highlights on your booking page
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Logo</Label>
                <div
                  className="border-2 border-dashed border-border rounded-lg p-6 sm:p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => logoInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = e.dataTransfer.files;
                    if (files.length > 0) {
                      handleLogoSelect({ target: { files } } as any);
                    }
                  }}
                >
                  {logoPreview ? (
                    <div className="space-y-4">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="max-h-32 mx-auto rounded-lg object-contain"
                      />
                      <div className="flex gap-2 justify-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            logoInputRef.current?.click();
                          }}
                        >
                          Change Logo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setLogoFile(null);
                            setLogoPreview("");
                            setLogoRemoved(true);
                            if (logoInputRef.current) {
                              logoInputRef.current.value = "";
                            }
                          }}
                        >
                          Remove
                        </Button>
                </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Drag and drop your logo here, or click to browse
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-4"
                        onClick={(e) => {
                          e.stopPropagation();
                          logoInputRef.current?.click();
                        }}
                      >
                        Upload Logo
                      </Button>
                    </>
                  )}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoSelect}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended: Square image, at least 200x200px, PNG or JPG format
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Booking page header media (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Add up to 6 images/videos for your booking page header carousel. If you leave this empty, we’ll use your service photos instead.
                </p>

                <div
                  className="border-2 border-dashed border-border rounded-lg p-4 sm:p-6 cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => headerMediaInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const files = e.dataTransfer.files;
                    if (files.length > 0) {
                      handleHeaderMediaSelect({ target: { files } } as any);
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      Drag and drop media here, or click to browse
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        headerMediaInputRef.current?.click();
                      }}
                    >
                      Add media
                    </Button>
                  </div>

                  <input
                    ref={headerMediaInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="hidden"
                    onChange={handleHeaderMediaSelect}
                  />

                  {headerMedia.length > 0 ? (
                    <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-3">
                      {headerMedia.map((m, idx) => (
                        <div key={m.id} className="relative group">
                          {m.type === "video" ? (
                            <video
                              src={m.preview}
                              className="h-20 w-full rounded-lg object-cover border border-border bg-muted"
                              muted
                              playsInline
                            />
                          ) : (
                            <img
                              src={m.preview}
                              alt={`Header media ${idx + 1}`}
                              className="h-20 w-full rounded-lg object-cover border border-border bg-muted"
                              loading="lazy"
                            />
                          )}

                          <div className="absolute inset-x-0 -bottom-2 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon"
                              className="h-7 w-7"
                              disabled={idx === 0}
                              onClick={(e) => {
                                e.stopPropagation();
                                moveHeaderMedia(idx, idx - 1);
                              }}
                              title="Move left"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon"
                              className="h-7 w-7"
                              disabled={idx === headerMedia.length - 1}
                              onClick={(e) => {
                                e.stopPropagation();
                                moveHeaderMedia(idx, idx + 1);
                              }}
                              title="Move right"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-background border border-border"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeHeaderMedia(idx);
                            }}
                            title="Remove media"
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">No header media added.</p>
                  )}

                  <p className="mt-3 text-xs text-muted-foreground">
                    Tip: the first item is the first image your customers see.
                  </p>
                </div>
              </div>

              {/* Additional logos (optional) */}
              <div className="space-y-2">
                <Label className="text-sm">Additional logos (optional)</Label>
                <p className="text-xs text-muted-foreground">
                  Add 2+ logos if your business has multiple brands/partners. These will show on public pages.
                </p>

                <div className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-foreground">Logos</div>
                    <div className="flex items-center gap-2">
                      <input
                        ref={extraLogoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0] || null;
                          setExtraLogoFile(f);
                          if (f) void handleAddExtraLogo(f);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isSavingExtraLogo}
                        onClick={() => extraLogoInputRef.current?.click()}
                      >
                        {isSavingExtraLogo ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Add logo
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {extraLogos.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {extraLogos.map((l: any) => (
                        <div key={String(l.id || l.url)} className="relative">
                          <img
                            src={String(l.url)}
                            alt={String(l.label || "Logo")}
                            className="h-14 w-14 rounded-lg object-cover border border-border bg-muted"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-background border border-border"
                            onClick={() => handleRemoveExtraLogo(l)}
                            title="Remove logo"
                          >
                            <X className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">No additional logos added.</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Google review link (optional)</Label>
                <Input
                  value={googleReviewUrl}
                  onChange={(e) => setGoogleReviewUrl(e.target.value)}
                  placeholder="https://g.page/r/XXXX/review"
                  className="input-sleek"
                />
                <p className="text-xs text-muted-foreground">
                  If set, after a customer submits a Buukd review they’ll be prompted to also leave a review on Google.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Client photos link (Google Drive / Dropbox)</Label>
                <Input
                  value={clientPhotosUrl}
                  onChange={(e) => setClientPhotosUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  className="input-sleek"
                />
                <p className="text-xs text-muted-foreground">
                  Optional. This link will be shown to clients after completion so they can download their photos.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Cancellation Policy</Label>
                <Textarea
                  placeholder="Enter your cancellation policy..."
                  className="input-sleek min-h-[100px]"
                  value={cancellationPolicy}
                  onChange={(e) => setCancellationPolicy(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  This policy will be displayed to customers on your booking page
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Terms and Conditions</Label>
                <Textarea
                  placeholder="Enter your terms and conditions..."
                  className="input-sleek min-h-[150px]"
                  value={termsAndConditions}
                  onChange={(e) => setTermsAndConditions(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Customers must agree to these terms before completing their booking. Leave empty to disable.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background p-4">
                  <div className="min-w-0">
                    <Label className="text-sm">Allow customers to book multiple services</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      When enabled, customers can select multiple services in one booking (max 1 per category). Staff is
                      auto-assigned based on availability.
                    </p>
                  </div>
                  <Switch checked={multiServiceBookingEnabled} onCheckedChange={setMultiServiceBookingEnabled} />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (business) {
                      const businessData = business as any;
                      setPrimaryColor(businessData.primary_color || "#2563EB");
                      setCancellationPolicy(businessData.cancellation_policy || "");
                      setTermsAndConditions(businessData.terms_and_conditions || "");
                      setGoogleReviewUrl(businessData.google_review_url ?? "");
                      setClientPhotosUrl(businessData.client_photos_url ?? "");
                      setEventCateringRequestsEnabled(!!(businessData as any).event_catering_requests_enabled);
                      setOrdersMultipleMenusEnabled(!!(businessData as any).orders_multiple_menus_enabled);
                      setSmsRemindersEnabled((businessData as any).sms_reminders_enabled !== false);
                      const accTheme = (businessData as any).accent_theme;
                      setAccentTheme(ACCENT_THEMES.includes(accTheme as AccentTheme) ? (accTheme as AccentTheme) : accTheme === "emerald" ? "lavender" : "blue");
                      setMultiServiceBookingEnabled(!!businessData.multi_service_booking_enabled);
                      if (businessData.logo_url) {
                        setLogoPreview(businessData.logo_url);
                      } else {
                        setLogoPreview("");
                      }
                      setLogoRemoved(false);
                      const header = (businessData as any)?.booking_header_media_items;
                      if (Array.isArray(header) && header.length > 0) {
                        setHeaderMedia(
                          header
                            .map((it: any, idx: number) => {
                              const url = String(it?.url || "").trim();
                              if (!url) return null;
                              const tRaw = String(it?.type || "").toLowerCase();
                              const lower = url.toLowerCase();
                              const type: "image" | "video" =
                                tRaw === "video" || tRaw === "image"
                                  ? (tRaw as any)
                                  : lower.endsWith(".mp4") ||
                                    lower.endsWith(".webm") ||
                                    lower.endsWith(".mov") ||
                                    lower.endsWith(".avi") ||
                                    lower.endsWith(".mkv")
                                  ? "video"
                                  : "image";
                              return {
                                id: `header-existing:${businessData.id}:${idx}:${url}`,
                                file: new File([], url.split("/").pop() || "existing-header-media"),
                                preview: url,
                                type,
                              };
                            })
                            .filter(Boolean) as Array<{ id: string; file: File; preview: string; type: "image" | "video" }>,
                        );
                      } else {
                        setHeaderMedia([]);
                      }
                      setLogoFile(null);
                      if (logoInputRef.current) {
                        logoInputRef.current.value = "";
                      }
                    }
                  }}
                  disabled={isSavingBooking}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveBookingSettings}
                  disabled={isSavingBooking}
                >
                  {isSavingBooking ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings Tab */}
        <TabsContent value="payment" className="space-y-4 sm:space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base sm:text-lg">Payments & Payouts</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Set up checkout for customers and where Buukd should send your settlements
                  </p>
                </div>
                {(business as any)?.payment_connected && (
                  <Badge className="bg-success/10 text-success border-success/20">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Payout connected
                  </Badge>
                )}
              </div>
              
              <div className="space-y-2">
                {!usesOwnPaystackCheckout ? (
                  <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background p-4">
                    <div className="min-w-0">
                      <Label className="text-sm">Online checkout for bookings (Buukd / Paystack)</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Turn this on so clients confirm bookings through Paystack—even when your services don&apos;t require a deposit. If disabled, clients pay you directly using your payment instructions.
                      </p>
                    </div>
                    <Switch checked={collectBookingDeposits} onCheckedChange={setCollectBookingDeposits} />
                  </div>
                ) : null}

                <div className="rounded-lg border border-border bg-background p-4 space-y-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <Label className="text-sm">Use your own Paystack account (optional)</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Connect your own Paystack account now, then turn on Buukd checkout whenever you are ready.
                        You must set your Paystack webhook to Buukd for automatic confirmation.
                      </p>
                      {businessPaystackMasked ? (
                        <p className="text-xs text-muted-foreground mt-2">
                          Connected: <span className="font-mono">{businessPaystackMasked}</span>
                        </p>
                      ) : null}
                    </div>
                    <Switch checked={businessPaystackEnabled} onCheckedChange={setBusinessPaystackEnabled} />
                  </div>

                  {businessPaystackEnabled ? (
                    <div className="space-y-2 border-t border-border pt-3">
                      <Label className="text-sm">Paystack Secret Key</Label>
                      <Input
                        className="input-sleek"
                        type="password"
                        placeholder={businessPaystackMasked ? "Paste a new key to replace (sk_live_...)" : "sk_live_..."}
                        value={businessPaystackSecretKey}
                        onChange={(e) => setBusinessPaystackSecretKey(e.target.value)}
                        autoComplete="off"
                      />
                      <p className="text-xs text-muted-foreground">
                        We store your key securely and never display it again. Paste a new key to replace the existing one.
                      </p>
                      <div className="rounded-lg border border-border bg-accent/40 p-3">
                        <p className="text-xs text-muted-foreground mb-1">Webhook URL</p>
                        <p className="text-xs font-mono break-all text-foreground">
                          {import.meta.env.VITE_SUPABASE_URL
                            ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paystack-webhook`
                            : "Set VITE_SUPABASE_URL to display your webhook URL"}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>

                {usesOnlineCheckout ? (
                  <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-background p-4">
                    <div className="min-w-0">
                      <Label className="text-sm">Allow full payment at booking</Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        When a deposit is required, clients can choose to pay the full amount upfront instead of only the deposit.
                      </p>
                    </div>
                    <Switch checked={allowFullPaymentAtBooking} onCheckedChange={setAllowFullPaymentAtBooking} />
                  </div>
                ) : null}

                {usesOnlineCheckout ? (
                  <div className="space-y-5 rounded-lg border border-border bg-background p-4">
                    <div className="space-y-1">
                      <Label className="text-sm">Connect payout account</Label>
                      <p className="text-xs text-muted-foreground">
                        Tell Buukd where to send your settlements after customers pay online. Choose Mobile Money or a bank account.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm">Region</Label>
                        <Select
                          value={paymentRegion}
                          onValueChange={(value) => {
                            const nextRegion = value as PaymentRegion;
                            setPaymentRegion(nextRegion);
                            setPaymentRegionManuallyChanged(true);
                            const nextMethods = REGION_METHODS[nextRegion].filter(
                              (method) => method === "bank" || method === "momo" || method === "mpesa",
                            );
                            setPaymentMethod(nextMethods[0] || "bank");
                            setPaymentBank("");
                            setPaymentMobileMoneyNetwork("");
                          }}
                        >
                          <SelectTrigger className="input-sleek">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_REGIONS.map((region) => (
                              <SelectItem key={region.value} value={region.value}>
                                {region.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Account type</Label>
                        <Select
                          value={payoutPaymentMethod}
                          onValueChange={(value) => {
                            setPaymentMethod(value);
                            setPaymentBank("");
                            setPaymentMobileMoneyNetwork("");
                          }}
                        >
                          <SelectTrigger className="input-sleek">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availablePayoutMethods.map((method) => (
                              <SelectItem key={method} value={method}>
                                {METHOD_LABELS[method]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {payoutPaymentMethod === "bank" ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-sm">Bank</Label>
                          <Select value={paymentBank || undefined} onValueChange={setPaymentBank}>
                            <SelectTrigger className="input-sleek">
                              <SelectValue placeholder={availableBanks.length ? "Select your bank" : "Select bank"} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableBanks.map((bank) => (
                                <SelectItem key={bank.value} value={bank.value}>
                                  {bank.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Account number</Label>
                          <Input
                            placeholder="Enter account number"
                            className="input-sleek"
                            value={paymentAccountNumber}
                            onChange={(e) => setPaymentAccountNumber(e.target.value)}
                            type="text"
                          />
                        </div>
                      </div>
                    ) : null}

                    {payoutPaymentMethod === "momo" || payoutPaymentMethod === "mpesa" ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        {availableMobileMoneyOptions.length > 0 ? (
                          <div className="space-y-2">
                            <Label className="text-sm">
                              {payoutPaymentMethod === "mpesa" ? "M-Pesa option" : "Mobile Money network"}
                            </Label>
                            <Select
                              value={paymentMobileMoneyNetwork || undefined}
                              onValueChange={setPaymentMobileMoneyNetwork}
                            >
                              <SelectTrigger className="input-sleek">
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableMobileMoneyOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : null}
                        <div className="space-y-2">
                          <Label className="text-sm">
                            {payoutPaymentMethod === "mpesa" ? "M-Pesa number" : "Mobile Money number"}
                          </Label>
                          <Input
                            placeholder="Enter payout number"
                            className="input-sleek"
                            value={paymentMobileMoneyNumber}
                            onChange={(e) => setPaymentMobileMoneyNumber(e.target.value)}
                            type="tel"
                          />
                        </div>
                      </div>
                    ) : null}

                    {payoutDestinationReady ? (
                      <div className="rounded-lg border border-success/20 bg-success/5 p-3 text-xs text-muted-foreground">
                        Settlements for this business will use this{" "}
                        {payoutPaymentMethod === "bank" ? "bank account" : "mobile money number"}.
                      </div>
                    ) : (
                      <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                        Add your MoMo or bank details and save so Buukd can pay out customer payments to you.
                      </div>
                    )}
                  </div>
                ) : null}

                {!usesOnlineCheckout ? (
                  <div className="space-y-5 rounded-lg border border-border bg-background p-4">
                    <div className="space-y-1">
                      <Label className="text-sm">Direct payment setup</Label>
                      <p className="text-xs text-muted-foreground">
                        We detected {PAYMENT_REGIONS.find((region) => region.value === detectedPaymentRegion)?.label || "your region"} from your location/currency. You can change it below.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-sm">Payment region</Label>
                        <Select
                          value={paymentRegion}
                          onValueChange={(value) => {
                            const nextRegion = value as PaymentRegion;
                            setPaymentRegion(nextRegion);
                            setPaymentRegionManuallyChanged(true);
                            const nextMethod = REGION_METHODS[nextRegion][0];
                            setPaymentMethod(nextMethod);
                            setPaymentBank("");
                            setPaymentMobileMoneyNetwork("");
                          }}
                        >
                          <SelectTrigger className="input-sleek">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_REGIONS.map((region) => (
                              <SelectItem key={region.value} value={region.value}>
                                {region.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Payment method</Label>
                        <Select
                          value={directPaymentMethod}
                          onValueChange={(value) => {
                            setPaymentMethod(value);
                            setPaymentBank("");
                            setPaymentMobileMoneyNetwork("");
                          }}
                        >
                          <SelectTrigger className="input-sleek">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {availableDirectMethods.map((method) => (
                              <SelectItem key={method} value={method}>
                                {METHOD_LABELS[method]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {directPaymentMethod === "bank" ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-sm">Bank</Label>
                          <Select value={paymentBank || undefined} onValueChange={setPaymentBank}>
                            <SelectTrigger className="input-sleek">
                              <SelectValue placeholder={availableBanks.length ? "Select your bank" : "Enter bank in instructions"} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableBanks.map((bank) => (
                                <SelectItem key={bank.value} value={bank.value}>
                                  {bank.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm">Account Number / IBAN</Label>
                          <Input
                            placeholder="Enter account number"
                            className="input-sleek"
                            value={paymentAccountNumber}
                            onChange={(e) => setPaymentAccountNumber(e.target.value)}
                            type="text"
                          />
                        </div>
                      </div>
                    ) : null}

                    {directPaymentMethod === "momo" || directPaymentMethod === "mpesa" ? (
                      <div className="grid gap-4 md:grid-cols-2">
                        {availableMobileMoneyOptions.length > 0 ? (
                          <div className="space-y-2">
                            <Label className="text-sm">{directPaymentMethod === "mpesa" ? "M-Pesa option" : "Mobile Money Network"}</Label>
                            <Select value={paymentMobileMoneyNetwork || undefined} onValueChange={setPaymentMobileMoneyNetwork}>
                              <SelectTrigger className="input-sleek">
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableMobileMoneyOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : null}

                        <div className="space-y-2">
                          <Label className="text-sm">{directPaymentMethod === "mpesa" ? "M-Pesa number" : "Mobile Money Number"}</Label>
                          <Input
                            placeholder="Enter payment number"
                            className="input-sleek"
                            value={paymentMobileMoneyNumber}
                            onChange={(e) => setPaymentMobileMoneyNumber(e.target.value)}
                            type="tel"
                          />
                        </div>
                      </div>
                    ) : null}

                    {directPaymentMethod === "venmo" || directPaymentMethod === "cashapp" ? (
                      <div className="space-y-2">
                        <Label className="text-sm">{directPaymentMethod === "venmo" ? "Venmo handle" : "Cash App tag"}</Label>
                        <Input
                          placeholder={directPaymentMethod === "venmo" ? "@your-handle" : "$yourcashtag"}
                          className="input-sleek"
                          value={paymentMobileMoneyNumber}
                          onChange={(e) => setPaymentMobileMoneyNumber(e.target.value)}
                        />
                      </div>
                    ) : null}

                    {directPaymentMethod !== "other" ? (
                      <div className="rounded-xl border border-border bg-muted/30 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-medium text-foreground">Customer preview</p>
                            <p className="text-xs text-muted-foreground">This is what customers will see after booking.</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setBookingDepositPaymentInstructions(generatedPaymentInstructions)}
                          >
                            Use this
                          </Button>
                        </div>
                        <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-background p-3 text-xs text-foreground">
                          {generatedPaymentInstructions}
                        </pre>
                      </div>
                    ) : null}

                    <div className="space-y-2">
                      <Label className="text-sm">Saved appointment payment instructions</Label>
                    <Textarea
                      className="input-sleek min-h-[120px]"
                      placeholder={`Example:\n- Pay deposit to MTN MoMo: 024xxxxxxx (Name: Buukd Catering)\n- Use your booking code as reference\n- Send screenshot to WhatsApp: +233...`}
                      value={bookingDepositPaymentInstructions}
                      onChange={(e) => setBookingDepositPaymentInstructions(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {isCateringModule
                        ? "For deposit-required appointment bookings: shown on the booking success page and in the customer email. If you leave the food/menu field below empty, this is also shown on the order success page and in order SMS."
                        : "For deposit-required appointment bookings: shown on the booking success page and in the customer email."}
                    </p>
                    </div>
                  </div>
                ) : null}

                {!usesOnlineCheckout && isCateringModule ? (
                  <div className="space-y-2 rounded-lg border border-border bg-background p-4">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm">Payment instructions for food / menu orders</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setFoodOrderPaymentInstructions(bookingDepositPaymentInstructions)}
                      >
                        Copy from above
                      </Button>
                    </div>
                    <Textarea
                      className="input-sleek min-h-[100px]"
                      placeholder="e.g. Pay via MTN MoMo 024xxxxxxx (ref: order code). Send screenshot to WhatsApp."
                      value={foodOrderPaymentInstructions}
                      onChange={(e) => setFoodOrderPaymentInstructions(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional. Shown on the order success page and in order confirmation SMS. If empty, the instructions above are used for orders too.
                    </p>
                  </div>
                ) : null}

                {/* Pay at venue: QR + link for clients to pay remaining balance after service */}
                {showPayAtVenue && (
                  <div className="space-y-2 rounded-lg border border-border bg-background p-4">
                    <Label className="text-sm">Pay at venue</Label>
                    <p className="text-xs text-muted-foreground">
                      Clients scan the QR code or open the link, enter their booking code, and pay the remaining balance (e.g. after a salon visit). Only shown when Paystack is set up.
                    </p>
                    {(() => {
                      const slug = String((business as any)?.slug || "");
                      const payUrl = slug ? `${getBaseUrl()}/pay/${slug}` : "";
                      const qrUrl = payUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(payUrl)}` : "";
                      return (
                        <div className="flex flex-wrap items-start gap-4 mt-2">
                          {qrUrl ? (
                            <div className="rounded-lg border border-border overflow-hidden bg-white p-2 shrink-0">
                              <img src={qrUrl} alt="QR code for pay link" width={180} height={180} className="block" />
                            </div>
                          ) : null}
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
                              <input
                                type="text"
                                readOnly
                                value={payUrl}
                                className="flex-1 bg-transparent text-sm text-foreground outline-none min-w-0"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-9 w-9 p-0 inline-flex items-center justify-center"
                                onClick={() => {
                                  if (payUrl && navigator.clipboard?.writeText) {
                                    navigator.clipboard.writeText(payUrl);
                                    toast({ title: "Link copied" });
                                  }
                                }}
                                aria-label="Copy pay link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              Print the QR code or share the link so clients can pay after their appointment.
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPaymentMethod((business as any)?.payment_method || "");
                    setPaymentBank((business as any)?.payment_bank || "");
                    setPaymentAccountNumber((business as any)?.payment_account_number || "");
                    setPaymentMobileMoneyNetwork((business as any)?.payment_mobile_money_network || "");
                    setPaymentMobileMoneyNumber((business as any)?.payment_mobile_money_number || "");
                    const restoredMethod = String((business as any)?.payment_method || "");
                    if (restoredMethod === "momo") setPaymentRegion("ghana");
                    if (restoredMethod === "mpesa") setPaymentRegion(currency === "ZAR" ? "south_africa" : "kenya");
                    if (restoredMethod === "venmo" || restoredMethod === "cashapp") setPaymentRegion("united_states");
                    setCollectBookingDeposits((business as any)?.collect_booking_deposits === true);
                    setAllowFullPaymentAtBooking((business as any)?.allow_full_payment_at_booking === true);
                    setBookingDepositPaymentInstructions((business as any)?.booking_deposit_payment_instructions || "");
                    setFoodOrderPaymentInstructions((business as any)?.food_order_payment_instructions || "");
                    setPaymentRegionManuallyChanged(false);
                    const psEnabled = !!(business as any)?.paystack_connected_account_enabled;
                    const psMode = String((business as any)?.paystack_connected_account_mode || "").trim();
                    const psLast4 = String((business as any)?.paystack_connected_account_last4 || "").trim();
                    setBusinessPaystackEnabled(psEnabled);
                    setBusinessPaystackMasked(
                      psEnabled && psLast4 ? `sk_${psMode || "live"}_••••${psLast4}` : "",
                    );
                    setBusinessPaystackSecretKey("");
                  }}
                  disabled={isSavingPayment}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!business) {
                      toast({
                        title: "Error",
                        description: "No business found",
                        variant: "destructive",
                      });
                      return;
                    }

                    const savedDirectPaymentInstructions =
                      !usesOnlineCheckout && directPaymentMethod !== "other" && !bookingDepositPaymentInstructions.trim()
                        ? generatedPaymentInstructions
                        : bookingDepositPaymentInstructions.trim();

                    // Validation
                    if (!usesOnlineCheckout && !savedDirectPaymentInstructions.trim()) {
                      toast({
                        title: "Validation Error",
                        description: "Please provide payment instructions (or turn Buukd deposit collection back on).",
                        variant: "destructive",
                      });
                      return;
                    }

                    // Business Paystack validation is independent from whether checkout is currently enabled.
                    if (businessPaystackEnabled) {
                      const hasExisting = !!(business as any)?.paystack_connected_account_enabled;
                      if (!hasExisting && !businessPaystackSecretKey.trim()) {
                        toast({
                          title: "Validation Error",
                          description: "Please paste your Paystack secret key (sk_live_... or sk_test_...).",
                          variant: "destructive",
                        });
                        return;
                      }
                    }

                    const payoutMethodForSave = usesOnlineCheckout ? payoutPaymentMethod : directPaymentMethod;
                    const savingBankPayout = payoutMethodForSave === "bank";
                    const savingMobilePayout =
                      payoutMethodForSave === "momo"
                      || payoutMethodForSave === "mpesa"
                      || (!usesOnlineCheckout && (payoutMethodForSave === "venmo" || payoutMethodForSave === "cashapp"));
                    const payoutDetailsStarted = usesOnlineCheckout && (
                      !!paymentBank.trim()
                      || !!paymentAccountNumber.trim()
                      || !!paymentMobileMoneyNetwork.trim()
                      || !!paymentMobileMoneyNumber.trim()
                    );

                    if (
                      (usesOnlineCheckout ? payoutDetailsStarted || payoutDestinationReady : true)
                      && savingBankPayout
                      && (!paymentBank || !paymentAccountNumber)
                    ) {
                      toast({
                        title: "Validation Error",
                        description: "Please select a bank and enter your account number",
                        variant: "destructive",
                      });
                      return;
                    }

                    if (
                      (usesOnlineCheckout ? payoutDetailsStarted || payoutDestinationReady : !usesOnlineCheckout)
                      && (payoutMethodForSave === "momo" || payoutMethodForSave === "mpesa")
                      && ((availableMobileMoneyOptions.length > 0 && !paymentMobileMoneyNetwork) || !paymentMobileMoneyNumber)
                    ) {
                      toast({
                        title: "Validation Error",
                        description: "Please select a provider and enter your payment number",
                        variant: "destructive",
                      });
                      return;
                    }

                    if (
                      !usesOnlineCheckout &&
                      (directPaymentMethod === "venmo" || directPaymentMethod === "cashapp") &&
                      !paymentMobileMoneyNumber.trim()
                    ) {
                      toast({
                        title: "Validation Error",
                        description: `Please enter your ${directPaymentMethod === "venmo" ? "Venmo handle" : "Cash App tag"}`,
                        variant: "destructive",
                      });
                      return;
                    }

                    setIsSavingPayment(true);
                    try {
                      const payoutConnected = usesOnlineCheckout
                        ? payoutDestinationReady
                        : true;

                      const updateData: any = {
                        payment_method: usesOnlineCheckout
                          ? (payoutConnected ? payoutPaymentMethod : null)
                          : directPaymentMethod,
                        payment_connected: payoutConnected,
                        collect_booking_deposits: !!usesOnlineCheckout,
                        allow_full_payment_at_booking: !!allowFullPaymentAtBooking,
                        booking_deposit_payment_instructions: usesOnlineCheckout
                          ? null
                          : savedDirectPaymentInstructions || null,
                        food_order_payment_instructions: foodOrderPaymentInstructions.trim() || null,
                      };

                      if (savingBankPayout && (!usesOnlineCheckout || payoutConnected)) {
                        updateData.payment_bank = paymentBank;
                        updateData.payment_account_number = paymentAccountNumber;
                        updateData.payment_mobile_money_network = null;
                        updateData.payment_mobile_money_number = null;
                      } else if (savingMobilePayout && (!usesOnlineCheckout || payoutConnected)) {
                        updateData.payment_mobile_money_network = paymentMobileMoneyNetwork || null;
                        updateData.payment_mobile_money_number = paymentMobileMoneyNumber;
                        updateData.payment_bank = null;
                        updateData.payment_account_number = null;
                      } else if (usesOnlineCheckout && !payoutConnected) {
                        updateData.payment_bank = null;
                        updateData.payment_account_number = null;
                        updateData.payment_mobile_money_network = null;
                        updateData.payment_mobile_money_number = null;
                      } else if (!usesOnlineCheckout) {
                        updateData.payment_bank = null;
                        updateData.payment_account_number = null;
                        updateData.payment_mobile_money_network = null;
                        updateData.payment_mobile_money_number = null;
                      }

                      const { error } = await supabase
                        .from("businesses")
                        .update(updateData)
                        .eq("id", business.id);

                      if (error) throw error;

                      // Connected Paystack account (BYO keys) is independent from the checkout toggle.
                      const wasEnabled = !!(business as any)?.paystack_connected_account_enabled;
                      if (businessPaystackEnabled) {
                        const key = businessPaystackSecretKey.trim();
                        if (key) {
                          const { error: rpcErr } = await (supabase as any).rpc(
                            "upsert_business_paystack_account",
                            { p_business_id: business.id, p_secret_key: key },
                          );
                          if (rpcErr) throw rpcErr;
                        }
                      } else if (wasEnabled) {
                        const { error: rpcErr } = await (supabase as any).rpc(
                          "disable_business_paystack_account",
                          { p_business_id: business.id },
                        );
                        if (rpcErr) throw rpcErr;
                      }

                      toast({
                        title: "Success",
                        description: usesOnlineCheckout && payoutConnected
                          ? "Payment settings and payout account saved"
                          : "Payment settings saved successfully",
                      });

                      await refetch();
                      setBusinessPaystackSecretKey("");
                    } catch (err: any) {
                      console.error("Error saving payment settings:", err);
                      
                      // Check if it's a schema error (columns don't exist)
                      if (err?.code === "PGRST204" || err?.message?.includes("Could not find")) {
                        toast({
                          title: "Database Migration Required",
                          description: "Please run the payment settings migration in your Supabase dashboard. Check MIGRATION_INSTRUCTIONS.md for details.",
                          variant: "destructive",
                        });
                      } else {
                        toast({
                          title: "Error",
                          description: err instanceof Error ? err.message : "Failed to save payment settings",
                          variant: "destructive",
                        });
                      }
                    } finally {
                      setIsSavingPayment(false);
                    }
                  }}
                  disabled={isSavingPayment || (!usesOnlineCheckout && !directPaymentMethod)}
                >
                  {isSavingPayment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coupons Tab */}
        <TabsContent value="coupons" className="space-y-4 sm:space-y-6">
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <div>
                <h3 className="font-semibold text-base sm:text-lg">Coupons</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Create simple discount codes customers can apply during booking.
                </p>
              </div>

              <div className="rounded-lg border border-border bg-background p-4 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Code</Label>
                    <Input
                      className="input-sleek"
                      placeholder="e.g. WELCOME10"
                      value={couponCodeNew}
                      onChange={(e) => setCouponCodeNew(e.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={couponTypeNew} onValueChange={(v: any) => setCouponTypeNew(v)}>
                      <SelectTrigger className="input-sleek">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Percent (%)</SelectItem>
                        <SelectItem value="amount">Amount ({currency})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Value</Label>
                    <Input
                      className="input-sleek"
                      inputMode="decimal"
                      placeholder={couponTypeNew === "percent" ? "10" : "20"}
                      value={couponValueNew}
                      onChange={(e) => setCouponValueNew(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {couponTypeNew === "percent" ? "Example: 10 = 10% off" : `Example: 20 = ${currency}20 off`}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Active</Label>
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2">
                      <span className="text-sm text-muted-foreground">Enable this coupon</span>
                      <Switch checked={couponActiveNew} onCheckedChange={setCouponActiveNew} />
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Starts (optional)</Label>
                    <Input type="date" className="input-sleek" value={couponStartsNew} onChange={(e) => setCouponStartsNew(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Ends (optional)</Label>
                    <Input type="date" className="input-sleek" value={couponEndsNew} onChange={(e) => setCouponEndsNew(e.target.value)} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Applies to services (optional)</Label>
                  <div className="rounded-lg border border-border p-3 max-h-44 overflow-y-auto space-y-2">
                    {(services || []).filter((s: any) => s?.active !== false).length === 0 ? (
                      <div className="text-sm text-muted-foreground">No services found.</div>
                    ) : (
                      (services || [])
                        .filter((s: any) => s?.active !== false)
                        .map((s: any) => {
                          const sid = String(s.id);
                          const checked = couponServiceIdsNew.includes(sid);
                          return (
                            <label key={sid} className="flex items-center justify-between gap-3 text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    const on = !!v;
                                    setCouponServiceIdsNew((prev) =>
                                      on ? Array.from(new Set([...prev, sid])) : prev.filter((x) => x !== sid),
                                    );
                                  }}
                                />
                                <span className="truncate text-foreground">{String(s.name || "Service")}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {currency} {Number(s.price_amount || 0).toFixed(2)}
                              </span>
                            </label>
                          );
                        })
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave unselected to apply to all services.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Applies to categories (optional)</Label>
                  <div className="rounded-lg border border-border p-3 max-h-44 overflow-y-auto space-y-2">
                    {(serviceCategories || []).filter((c: any) => c?.active !== false).length === 0 ? (
                      <div className="text-sm text-muted-foreground">No categories found.</div>
                    ) : (
                      (serviceCategories || [])
                        .filter((c: any) => c?.active !== false)
                        .map((c: any) => {
                          const cid = String(c.id);
                          const checked = couponCategoryIdsNew.includes(cid);
                          return (
                            <label key={cid} className="flex items-center justify-between gap-3 text-sm">
                              <div className="flex items-center gap-2 min-w-0">
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    const on = !!v;
                                    setCouponCategoryIdsNew((prev) =>
                                      on ? Array.from(new Set([...prev, cid])) : prev.filter((x) => x !== cid),
                                    );
                                  }}
                                />
                                <span className="truncate text-foreground">{String(c.name || "Category")}</span>
                              </div>
                            </label>
                          );
                        })
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Leave unselected to apply to all categories.
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    disabled={isSavingCoupon || !business?.id}
                    onClick={async () => {
                      if (!business?.id) return;
                      const code = couponCodeNew.trim().toUpperCase();
                      if (!code) {
                        toast({ title: "Code required", description: "Enter a coupon code.", variant: "destructive" });
                        return;
                      }
                      const valueNum = Number(couponValueNew);
                      if (!Number.isFinite(valueNum) || valueNum <= 0) {
                        toast({ title: "Invalid value", description: "Enter a valid discount value.", variant: "destructive" });
                        return;
                      }
                      if (couponTypeNew === "percent" && valueNum > 100) {
                        toast({ title: "Invalid percent", description: "Percent must be 100 or less.", variant: "destructive" });
                        return;
                      }

                      const startsAt = couponStartsNew ? new Date(`${couponStartsNew}T00:00:00`).toISOString() : null;
                      const endsAt = couponEndsNew ? new Date(`${couponEndsNew}T23:59:59.999`).toISOString() : null;

                      setIsSavingCoupon(true);
                      try {
                        const { data: created, error } = await supabase.from("business_coupons" as any).insert({
                          business_id: business.id,
                          code,
                          discount_type: couponTypeNew,
                          discount_value: valueNum,
                          active: !!couponActiveNew,
                          starts_at: startsAt,
                          ends_at: endsAt,
                        } as any).select("id").single();
                        if (error) throw error;

                        // Optional: service scoping
                        const couponId = String((created as any)?.id || "");
                        if (couponId && couponServiceIdsNew.length > 0) {
                          const rows = couponServiceIdsNew.map((sid) => ({ coupon_id: couponId, service_id: sid }));
                          const { error: mapErr } = await supabase.from("business_coupon_services" as any).insert(rows as any);
                          if (mapErr) throw mapErr;
                        }

                        // Optional: category scoping
                        if (couponId && couponCategoryIdsNew.length > 0) {
                          const rows = couponCategoryIdsNew.map((cid) => ({ coupon_id: couponId, category_id: cid }));
                          const { error: mapErr } = await supabase.from("business_coupon_categories" as any).insert(rows as any);
                          if (mapErr) throw mapErr;
                        }

                        toast({ title: "Coupon created" });
                        setCouponCodeNew("");
                        setCouponValueNew("");
                        setCouponStartsNew("");
                        setCouponEndsNew("");
                        setCouponActiveNew(true);
                        setCouponServiceIdsNew([]);
                        setCouponCategoryIdsNew([]);
                        await fetchCoupons();
                      } catch (err: any) {
                        console.error("Failed to create coupon:", err);
                        if (err?.code === "PGRST204" || String(err?.message || "").includes("Could not find")) {
                          toast({
                            title: "Database Migration Required",
                            description: "Please run the coupons migration in your Supabase project.",
                            variant: "destructive",
                          });
                        } else {
                          toast({ title: "Failed to create coupon", description: err?.message || "Try again.", variant: "destructive" });
                        }
                      } finally {
                        setIsSavingCoupon(false);
                      }
                    }}
                  >
                    {isSavingCoupon ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Create coupon"
                    )}
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <p className="font-medium text-sm">Your coupons</p>
                  <p className="text-xs text-muted-foreground">{couponsLoading ? "Loading..." : `${(coupons || []).length} total`}</p>
                </div>
                <div className="p-2">
                  {couponsLoading ? (
                    <div className="p-4 text-sm text-muted-foreground">Loading…</div>
                  ) : (coupons || []).length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">No coupons yet.</div>
                  ) : (
                    <div className="space-y-1">
                      {(coupons || []).map((c) => (
                        <div
                          key={c.id}
                          className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm text-foreground truncate">{String(c.code || "")}</p>
                              <Badge variant={c.active ? "default" : "secondary"} className="text-xs">
                                {c.active ? "Active" : "Off"}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {String(c.discount_type) === "percent"
                                ? `${Number(c.discount_value || 0)}% off`
                                : `${currency} ${Number(c.discount_value || 0)} off`}
                              {(() => {
                                const scoped = couponServiceIdsByCouponId[String(c.id)] || [];
                                if (!scoped.length) return " · All services";
                                return ` · ${scoped.length} service${scoped.length === 1 ? "" : "s"}`;
                              })()}
                              {(() => {
                                const scopedCats = couponCategoryIdsByCouponId[String(c.id)] || [];
                                if (!scopedCats.length) return "";
                                return ` · ${scopedCats.length} categor${scopedCats.length === 1 ? "y" : "ies"}`;
                              })()}
                              {(() => {
                                const uses = couponUsesByCouponId[String(c.id)];
                                if (!uses) return "";
                                return ` · ${uses.total} use${uses.total === 1 ? "" : "s"}`;
                              })()}
                              {c.starts_at || c.ends_at ? (
                                <>
                                  {" "}
                                  ·{" "}
                                  {c.starts_at ? `from ${format(new Date(String(c.starts_at)), "MMM d, yyyy")}` : ""}
                                  {c.ends_at ? `${c.starts_at ? " " : ""}until ${format(new Date(String(c.ends_at)), "MMM d, yyyy")}` : ""}
                                </>
                              ) : null}
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            <Switch
                              checked={!!c.active}
                              onCheckedChange={async (next) => {
                                try {
                                  const { error } = await supabase
                                    .from("business_coupons" as any)
                                    .update({ active: !!next } as any)
                                    .eq("id", c.id);
                                  if (error) throw error;
                                  await fetchCoupons();
                                } catch (err: any) {
                                  toast({ title: "Failed to update", description: err?.message || "Try again.", variant: "destructive" });
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={async () => {
                                if (!confirm(`Delete coupon "${String(c.code || "")}"?`)) return;
                                try {
                                  const { error } = await supabase.from("business_coupons" as any).delete().eq("id", c.id);
                                  if (error) throw error;
                                  await fetchCoupons();
                                } catch (err: any) {
                                  toast({ title: "Failed to delete", description: err?.message || "Try again.", variant: "destructive" });
                                }
                              }}
                              aria-label="Delete coupon"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Locations Tab */}
        <TabsContent value="locations" className="space-y-4 sm:space-y-6">
          {(() => {
            const planId = String((business as any)?.plan_id || "starter");
            const locationLimit = currentPlan?.location_limit ?? null;
            const canAddMore = locationLimit === null || locations.length < locationLimit;
            const isAtLocationLimit =
              locationLimit !== null && typeof locationLimit === "number" && locations.length >= locationLimit;
            const isStarterAtLimit = planId === "starter" && isAtLocationLimit;

            const onAddLocationClick = () => {
              if (!canAddMore) {
                toast({
                  title: "Location Limit Reached",
                  description:
                    isNativeIos
                      ? "Please contact Buukd support for help adding more locations."
                      : planId === "starter"
                      ? "Starter plan allows only 1 location. Upgrade to Professional plan to add more locations."
                      : `You've reached your plan's location limit (${locationLimit}). Upgrade your plan to add more locations.`,
                  variant: "destructive",
                });
                return;
              }
              handleOpenLocationDialog();
            };

            return (
              <Card className="overflow-hidden border-border/80 shadow-sm">
                <CardContent className="space-y-4 p-4 sm:p-6">
                  <div className="flex w-full sm:justify-end">
                    <Button
                      type="button"
                      onClick={onAddLocationClick}
                      size="default"
                      className="w-full sm:w-auto"
                      disabled={!canAddMore}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add location
                    </Button>
                  </div>

                  {isStarterAtLimit && (
                    <Alert className="border-amber-200/80 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-50 [&>svg]:text-amber-700 dark:[&>svg]:text-amber-300">
                      <Info className="h-4 w-4" />
                      <AlertTitle className="text-amber-950 dark:text-amber-50">Location limit reached</AlertTitle>
                      <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
                        {isNativeIos ? (
                          <>
                            Starter includes one location.{" "}
                            <button
                              type="button"
                              onClick={() => openBuukdSupportWhatsApp("Hi Buukd support, I need help adding more locations.")}
                              className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-white"
                            >
                              Contact support
                            </button>{" "}
                            for help adding more.
                          </>
                        ) : (
                          <>
                            Starter includes one location.{" "}
                            <Link
                              to="/app/billing"
                              className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-white"
                            >
                              Upgrade to Professional
                            </Link>{" "}
                            for up to five locations.
                          </>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {!isStarterAtLimit && isAtLocationLimit && (
                    <Alert className="border-amber-200/80 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-50 [&>svg]:text-amber-700 dark:[&>svg]:text-amber-300">
                      <Info className="h-4 w-4" />
                      <AlertTitle className="text-amber-950 dark:text-amber-50">Location limit reached</AlertTitle>
                      <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
                        {isNativeIos ? (
                          <>
                            Your current plan allows {locationLimit} location{locationLimit === 1 ? "" : "s"}.{" "}
                            <button
                              type="button"
                              onClick={() => openBuukdSupportWhatsApp("Hi Buukd support, I need help adding more locations.")}
                              className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-white"
                            >
                              Contact support
                            </button>{" "}
                            to add more.
                          </>
                        ) : (
                          <>
                            Your current plan allows {locationLimit} location{locationLimit === 1 ? "" : "s"}.{" "}
                            <Link
                              to="/app/billing"
                              className="font-medium underline underline-offset-2 hover:text-amber-950 dark:hover:text-white"
                            >
                              Upgrade in Billing
                            </Link>{" "}
                            to add more.
                          </>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-3">
                  {locationsLoading ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-14 text-muted-foreground">
                      <Loader2 className="h-8 w-8 animate-spin" />
                      <p className="text-sm">Loading locations…</p>
                    </div>
                  ) : locations.length === 0 ? (
                    <div className="flex flex-col items-center rounded-xl border border-dashed border-border/80 bg-muted/15 px-4 py-12 text-center">
                      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                        <MapPin className="h-7 w-7 text-muted-foreground" />
                      </div>
                      <p className="text-base font-medium text-foreground">No locations yet</p>
                      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                        Add your main address so customers and staff know where you operate.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        className="mt-6"
                        onClick={onAddLocationClick}
                        disabled={!canAddMore}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add your first location
                      </Button>
                    </div>
                  ) : (
                    <ul className="space-y-2.5" role="list">
                      {locations.map((location) => {
                        const loc = location as Tables<"locations"> & {
                          is_default?: boolean;
                          city?: string | null;
                          country?: string | null;
                        };
                        const cityLine =
                          loc.city && loc.country
                            ? `${loc.city}, ${loc.country}`
                            : loc.city || loc.country || "";
                        const addressPrimary = (loc.address || "").trim();
                        const showSecondary =
                          !!addressPrimary &&
                          !!cityLine &&
                          !addressPrimary.toLowerCase().includes(String(loc.city || "").toLowerCase());

                        return (
                          <li
                            key={location.id}
                            className="flex gap-3 rounded-xl border border-border/80 bg-muted/25 p-3.5 transition-colors hover:bg-muted/40 sm:p-4"
                          >
                            <div
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-background shadow-sm ring-1 ring-border/60"
                              aria-hidden
                            >
                              <MapPin className="h-5 w-5 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 gap-y-1">
                                <span className="truncate text-sm font-semibold text-foreground sm:text-base">
                                  {location.name}
                                </span>
                                {loc.is_default && (
                                  <Badge variant="secondary" className="text-[10px] font-medium uppercase tracking-wide">
                                    Default
                                  </Badge>
                                )}
                              </div>
                              {addressPrimary ? (
                                <p className="mt-1 text-xs leading-snug text-muted-foreground sm:text-sm">{addressPrimary}</p>
                              ) : !addressPrimary && cityLine ? (
                                <p className="mt-1 text-xs leading-snug text-muted-foreground sm:text-sm">{cityLine}</p>
                              ) : null}
                              {showSecondary ? (
                                <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">{cityLine}</p>
                              ) : null}
                            </div>
                            <div className="flex shrink-0 items-start pt-0.5">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-muted-foreground hover:text-foreground"
                                    aria-label={`Location actions for ${location.name}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem onClick={() => handleOpenLocationDialog(location)}>
                                    <Edit2 className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  {!loc.is_default && (
                                    <DropdownMenuItem onClick={() => handleSetDefaultLocation(location.id)}>
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Set as default
                                    </DropdownMenuItem>
                                  )}
                                  {!loc.is_default && (
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteLocation(location.id)}
                                      className="text-destructive focus:text-destructive"
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Location Dialog */}
          <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
            <DialogContent className="sm:max-w-2xl w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingLocationId ? "Edit Location" : "Add New Location"}</DialogTitle>
                <DialogDescription>
                  {editingLocationId
                    ? "Update the location details below."
                    : "Add a new location for your business. You can assign services and staff to specific locations."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="location-name">Location Name *</Label>
                  <Input
                    id="location-name"
                    placeholder="e.g., Main Branch, Downtown Office"
                    value={locationFormName}
                    onChange={(e) => setLocationFormName(e.target.value)}
                    disabled={isSavingLocation}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location-address">Address *</Label>
                  <MapPicker
                    address={locationFormAddress}
                    onAddressChange={(v) => {
                      setLocationFormAddress(v);
                      setLocationFormPlaceId("");
                      setLocationFormLat(null);
                      setLocationFormLng(null);
                      setLocationFormCity("");
                      setLocationFormCountry("");
                    }}
                    lat={locationFormLat}
                    lng={locationFormLng}
                    onLatLngChange={(pos) => {
                      setLocationFormLat(pos?.lat ?? null);
                      setLocationFormLng(pos?.lng ?? null);
                    }}
                    onPlaceSelected={(p) => {
                      setLocationFormAddress(p.formattedAddress);
                      setLocationFormPlaceId(p.placeId || "");
                      setLocationFormCity(p.city || "");
                      setLocationFormCountry(p.country || "");
                      setLocationFormLat(typeof p.lat === "number" ? p.lat : null);
                      setLocationFormLng(typeof p.lng === "number" ? p.lng : null);
                    }}
                    disabled={isSavingLocation}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setLocationDialogOpen(false);
                    resetLocationForm();
                  }}
                  disabled={isSavingLocation}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveLocation} disabled={isSavingLocation}>
                  {isSavingLocation ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : editingLocationId ? (
                    "Update Location"
                  ) : (
                    "Add Location"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4 sm:space-y-6">
          <EnablePushNotifications />
          <Card>
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              <h3 className="font-semibold text-base sm:text-lg">Notification Settings</h3>
              
              <div className="flex items-start sm:items-center justify-between py-3 border-b border-border gap-3">
                <div className="min-w-0 flex-1">
                  <Label className="text-sm">Email Reminders</Label>
                  <p className="text-xs sm:text-sm text-muted-foreground">Send email reminders to customers before appointments</p>
                </div>
                <Switch defaultChecked className="flex-shrink-0" />
              </div>

              <div className="flex items-start sm:items-center justify-between py-3 border-b border-border gap-3">
                <div className="min-w-0 flex-1">
                  <Label className="text-sm">SMS Reminders</Label>
                  <p className="text-xs sm:text-sm text-muted-foreground">Send SMS reminders to customers and to you before appointments (24h and 2h)</p>
                </div>
                <Switch checked={smsRemindersEnabled} onCheckedChange={setSmsRemindersEnabled} className="flex-shrink-0" />
              </div>

              <div className="flex items-start sm:items-center justify-between py-3 border-b border-border gap-3">
                <div className="min-w-0 flex-1">
                  <Label className="text-sm">Review Requests</Label>
                  <p className="text-xs sm:text-sm text-muted-foreground">Send review requests after completed appointments</p>
                </div>
                <Switch defaultChecked className="flex-shrink-0" />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Reminder Hours Before</Label>
                <Select defaultValue="24">
                  <SelectTrigger className="input-sleek w-full sm:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="2">2 hours</SelectItem>
                    <SelectItem value="12">12 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="48">48 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t border-border">
                <Button onClick={handleSaveSettings} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save changes"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Tab */}
        <TabsContent value="support" className="space-y-4 sm:space-y-6">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardContent className="p-4 sm:p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-primary/10 p-2.5 text-primary">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">Email Buukd support</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Tell us what happened and we’ll send your request directly to hello@buukd.co.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="support-business-name">Business name</Label>
                    <Input
                      id="support-business-name"
                      value={supportBusinessName}
                      onChange={(e) => setSupportBusinessName(e.target.value)}
                      placeholder="Your business name"
                      className="input-sleek h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="support-message">Describe the problem</Label>
                    <Textarea
                      id="support-message"
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                      placeholder="Tell us what you were trying to do, what went wrong, and any error message you saw."
                      className="input-sleek min-h-[150px] rounded-2xl"
                    />
                  </div>
                </div>

                <Button
                  type="button"
                  className="w-full sm:w-auto gap-2"
                  onClick={openSupportEmail}
                  disabled={supportSending}
                >
                  {supportSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                  {supportSending ? "Sending..." : "Send to hello@buukd.co"}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 sm:p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-emerald-500/10 p-2.5 text-emerald-600">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">WhatsApp customer care</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Need faster help? Chat with Buukd customer care on WhatsApp.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-muted/30 p-4">
                  <p className="text-sm font-medium text-foreground">Customer care number</p>
                  <p className="mt-1 text-sm text-muted-foreground">+233 54 127 3802</p>
                </div>

                <Button asChild className="w-full sm:w-auto gap-2">
                  <a
                    href="https://wa.me/233541273802"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat on WhatsApp
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 sm:p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-amber-500/10 p-2.5 text-amber-600">
                    <Star className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg text-foreground">Rate Buukd</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Enjoying Buukd? Leave a quick rating on the app store to help other businesses find us.
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-muted/30 p-4">
                  <p className="text-sm font-medium text-foreground">Share your feedback publicly</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    We’ll open the right store for your device.
                  </p>
                </div>

                <Button type="button" variant="outline" className="w-full sm:w-auto gap-2" onClick={openAppRating}>
                  <Star className="h-4 w-4" />
                  Rate Buukd
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Add / edit staff dialog */}
      <Dialog
        open={isStaffDialogOpen}
        onOpenChange={(open) => {
          setIsStaffDialogOpen(open);
          if (!open) resetStaffForm();
        }}
      >
        <DialogContent className="sm:max-w-[425px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>
              {editingStaffId
                ? isCateringModule
                  ? "Edit rider"
                  : "Edit staff member"
                : isCateringModule
                  ? "Add rider"
                  : "Add staff member"}
            </DialogTitle>
            <DialogDescription>
              {editingStaffId
                ? "Update their details. For portal access, service locations, and richer options, use the Staff page."
                : isCateringModule
                  ? "Add a delivery team member. They can be assigned to orders and routes."
                  : "Add someone to your team. They appear in booking assignment and reports."}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Enter staff name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                type="tel"
                value={formPhone}
                onChange={(e) => setFormPhone(e.target.value)}
                placeholder="+233 XX XXX XXXX"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={formRole} onValueChange={(value: AppRole) => setFormRole(value)}>
                <SelectTrigger id="edit-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setIsStaffDialogOpen(false);
                resetStaffForm();
              }}
              disabled={isSaving}
              className="flex-1 rounded-lg"
            >
              Cancel
            </Button>
            <Button onClick={handleSaveStaff} disabled={isSaving || !formName.trim()} className="flex-1 rounded-lg">
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : editingStaffId ? (
                "Save changes"
              ) : isCateringModule ? (
                "Add rider"
              ) : (
                "Add staff"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service-specific weekly hours */}
      <Dialog
        open={serviceHoursDialogOpen}
        onOpenChange={(open) => {
          setServiceHoursDialogOpen(open);
          if (!open) {
            setServiceHoursServiceId("");
            setServiceHours([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[650px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Service-specific hours</DialogTitle>
            <DialogDescription>
              Choose the weekdays and times this service can be booked every week.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0 space-y-4">
            <div className="space-y-2">
              <Label>Service</Label>
              <Select
                value={serviceHoursServiceId}
                onValueChange={(value) => {
                  setServiceHoursServiceId(value);
                  void loadServiceHours(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {(services || [])
                    .filter((service: any) => service?.active !== false)
                    .map((service) => (
                      <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {serviceHoursLoading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-3">
                {serviceHours.map((daySchedule, index) => (
                  <div key={daySchedule.day_of_week} className="p-3 sm:p-4 border border-border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm sm:text-base">
                          <span className="sm:hidden">{DAYS_SHORT[index]}</span>
                          <span className="hidden sm:inline">{DAYS[index]}</span>
                        </span>
                        {!daySchedule.is_off && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">Open</span>
                        )}
                      </div>
                      <Switch
                        checked={!daySchedule.is_off}
                        onCheckedChange={(checked) => {
                          setServiceHours((previous) => previous.map((hour, i) =>
                            i === index ? { ...hour, is_off: checked !== true } : hour
                          ));
                        }}
                      />
                    </div>

                    {!daySchedule.is_off && (
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="space-y-1 min-w-0">
                          <Label className="text-xs text-muted-foreground">Starts</Label>
                          <div className="flex items-center gap-1.5 sm:gap-2 border border-border rounded-lg px-2 sm:px-3 py-2">
                            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                            <Input
                              type="time"
                              value={daySchedule.start_time}
                              onChange={(event) => setServiceHours((previous) => previous.map((hour, i) =>
                                i === index ? { ...hour, start_time: event.target.value } : hour
                              ))}
                              className="border-0 p-0 h-auto min-w-0 text-xs sm:text-sm focus-visible:ring-0"
                            />
                          </div>
                        </div>
                        <div className="space-y-1 min-w-0">
                          <Label className="text-xs text-muted-foreground">Ends</Label>
                          <div className="flex items-center gap-1.5 sm:gap-2 border border-border rounded-lg px-2 sm:px-3 py-2">
                            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground flex-shrink-0" />
                            <Input
                              type="time"
                              value={daySchedule.end_time}
                              onChange={(event) => setServiceHours((previous) => previous.map((hour, i) =>
                                i === index ? { ...hour, end_time: event.target.value } : hour
                              ))}
                              className="border-0 p-0 h-auto min-w-0 text-xs sm:text-sm focus-visible:ring-0"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Leave every day unselected to use the regular business hours.
            </p>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-col sm:flex-row gap-3 flex-shrink-0">
            <Button
              variant="ghost"
              onClick={clearServiceHours}
              disabled={isSaving || serviceHoursLoading || !serviceHoursServiceId}
              className="sm:mr-auto"
            >
              Use regular business hours
            </Button>
            <Button variant="outline" onClick={() => setServiceHoursDialogOpen(false)} disabled={isSaving} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={saveServiceHours}
              disabled={isSaving || serviceHoursLoading || !serviceHoursServiceId}
              className="flex-1"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save hours
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Hours Dialog */}
      <Dialog 
        open={isHoursDialogOpen} 
        onOpenChange={(open) => {
          setIsHoursDialogOpen(open);
          if (!open) {
            setManagingHoursStaffId(null);
            setStaffHours([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Manage Working Hours</DialogTitle>
            <DialogDescription>
              Set the working hours for this staff member. Uncheck days they don't work.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-4">
              {staffHours.map((daySchedule, index) => (
                <div key={index} className="flex items-center gap-4 p-3 border rounded-lg">
                <div className="flex items-center gap-2 min-w-[100px]">
                  <Checkbox
                    id={`day-${index}`}
                    checked={!daySchedule.is_off}
                    onCheckedChange={(checked) => {
                      setStaffHours(prev =>
                        prev.map((h, i) =>
                          i === index ? { ...h, is_off: !checked } : h
                        )
                      );
                    }}
                  />
                  <Label htmlFor={`day-${index}`} className="font-medium cursor-pointer">
                    {DAYS[index]}
                  </Label>
                </div>
                {!daySchedule.is_off && (
                  <div className="flex items-center gap-2 flex-1">
                    <div className="flex-1">
                      <Label htmlFor={`start-${index}`} className="text-xs text-muted-foreground">
                        Start Time
                      </Label>
                      <Input
                        id={`start-${index}`}
                        type="time"
                        value={daySchedule.start_time}
                        onChange={(e) => {
                          setStaffHours(prev =>
                            prev.map((h, i) =>
                              i === index ? { ...h, start_time: e.target.value } : h
                            )
                          );
                        }}
                        className="mt-1"
                      />
                    </div>
                    <span className="pt-6">-</span>
                    <div className="flex-1">
                      <Label htmlFor={`end-${index}`} className="text-xs text-muted-foreground">
                        End Time
                      </Label>
                      <Input
                        id={`end-${index}`}
                        type="time"
                        value={daySchedule.end_time}
                        onChange={(e) => {
                          setStaffHours(prev =>
                            prev.map((h, i) =>
                              i === index ? { ...h, end_time: e.target.value } : h
                            )
                          );
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
                {daySchedule.is_off && (
                  <span className="text-sm text-muted-foreground">Day off</span>
                )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setIsHoursDialogOpen(false);
                setManagingHoursStaffId(null);
              }}
              disabled={isSaving}
              className="flex-1 rounded-lg"
            >
              Cancel
            </Button>
            <Button onClick={handleSaveStaffHours} disabled={isSaving} className="flex-1 rounded-lg">
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Hours"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Services Dialog */}
      <Dialog 
        open={isServicesDialogOpen} 
        onOpenChange={(open) => {
          setIsServicesDialogOpen(open);
          if (!open) {
            setAssigningServicesStaffId(null);
            setAssignedServiceIds([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Assign Services</DialogTitle>
            <DialogDescription>
              Select which services this staff member can provide.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-3">
              {services.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No services available. Create services first.
                </p>
              ) : (
                services.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                  <Checkbox
                    id={`service-${service.id}`}
                    checked={assignedServiceIds.includes(service.id)}
                    onCheckedChange={() => toggleServiceAssignment(service.id)}
                  />
                  <Label
                    htmlFor={`service-${service.id}`}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium">{service.name}</div>
                    {service.description && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {service.description}
                      </div>
                    )}
                  </Label>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setIsServicesDialogOpen(false);
                setAssigningServicesStaffId(null);
              }}
              disabled={isSaving}
              className="flex-1 rounded-lg"
            >
              Cancel
            </Button>
            <Button onClick={handleSaveStaffServices} disabled={isSaving} className="flex-1 rounded-lg">
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Assignments"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Break Time Dialog */}
      <Dialog open={breakTimeDialogOpen} onOpenChange={setBreakTimeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingBreakTime ? "Edit Break Time" : "Add Break Time"}
            </DialogTitle>
            <DialogDescription>
              {breakTimeDialogDay !== null && `Set break time for ${DAYS[breakTimeDialogDay]}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label htmlFor="break-name">Name (optional)</Label>
              <Input
                id="break-name"
                placeholder="e.g., Lunch Break, Cleaning Time"
                value={breakTimeForm.name || ""}
                onChange={(e) => setBreakTimeForm({ ...breakTimeForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Time Range</Label>
              <div className="flex items-center gap-2">
                {/* Start Time */}
                <label
                  htmlFor="break-start"
                  className="flex-1 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 cursor-pointer hover:border-primary/50 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-colors"
                >
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide leading-none mb-1">Start</p>
                    <input
                      id="break-start"
                      type="time"
                      value={breakTimeForm.startTime}
                      onChange={(e) => setBreakTimeForm({ ...breakTimeForm, startTime: e.target.value })}
                      className="w-full bg-transparent text-sm font-semibold text-foreground outline-none"
                    />
                  </div>
                </label>

                <span className="text-muted-foreground text-sm font-medium flex-shrink-0">→</span>

                {/* End Time */}
                <label
                  htmlFor="break-end"
                  className="flex-1 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 cursor-pointer hover:border-primary/50 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary transition-colors"
                >
                  <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide leading-none mb-1">End</p>
                    <input
                      id="break-end"
                      type="time"
                      value={breakTimeForm.endTime}
                      onChange={(e) => setBreakTimeForm({ ...breakTimeForm, endTime: e.target.value })}
                      className="w-full bg-transparent text-sm font-semibold text-foreground outline-none"
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setBreakTimeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveBreakTime}>
              {editingBreakTime ? "Save Changes" : "Add Break"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
